import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import shadcn components
import { Button } from './components/ui/button.jsx';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card.jsx';
import { Input } from './components/ui/input.jsx';
import { Label } from './components/ui/label.jsx';
import { Textarea } from './components/ui/textarea.jsx';
import { Badge } from './components/ui/badge.jsx';
import { Avatar, AvatarFallback } from './components/ui/avatar.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs.jsx';
import { Alert, AlertDescription } from './components/ui/alert.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';

// Import Lucide icons
import { 
  TrendingUp, 
  Shield, 
  Award, 
  Phone, 
  Mail, 
  MapPin, 
  User, 
  ShoppingCart, 
  BarChart3, 
  Users, 
  MessageSquare,
  Crown,
  Coins,
  Mountain,
  Pickaxe,
  Star,
  Clock,
  CheckCircle,
  Package,
  Truck,
  Trash2,
  Eye,
  EyeOff,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      // Validate token and get user info
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setUser(response.data);
    } catch (error) {
      console.error('Token validation failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper function to format product names
const formatProductName = (productType) => {
  if (productType === '1_kilo_bar') return '1 Kilo Gold Bar';
  if (productType === '12.5_kilo_bar') return '12.5 Kilo Gold Bar';
  return productType; // fallback for any other types
};

// Header Component
const Header = () => {
  const { user, logout } = useAuth();
  const [goldPrice, setGoldPrice] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    fetchGoldPrice();
    const priceInterval = setInterval(fetchGoldPrice, 3000); // Update every 3 seconds to match goldapi.io
    
    // Force re-render every second to update the "time since last update" display
    const displayInterval = setInterval(() => {
      setCurrentTime(new Date()); // This forces a re-render every second
    }, 1000);
    
    return () => {
      clearInterval(priceInterval);
      clearInterval(displayInterval);
    };
  }, []);

  const fetchGoldPrice = async () => {
    try {
      console.log('🔄 Fetching gold price...', new Date().toLocaleTimeString());
      // Add cache-busting parameter to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/gold/current-price?t=${timestamp}`);
      console.log('✅ Gold price fetched:', response.data);
      // Update the timestamp to current time for display purposes
      const updatedGoldPrice = {
        ...response.data,
        displayTimestamp: new Date()
      };
      setGoldPrice(updatedGoldPrice);
    } catch (error) {
      console.error('❌ Error fetching gold price:', error);
      console.error('API URL:', `${API}/gold/current-price`);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-amber-500/20">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-amber-400 to-yellow-600 p-2 rounded-lg">
              <Crown className="h-6 w-6 text-black" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Aurelia Prosperity</h1>
              <p className="text-xs text-amber-400">Premium Gold Bullion</p>
            </div>
          </div>

          {/* Live Gold Price */}
          {goldPrice && (
            <div className="hidden md:flex items-center space-x-1 bg-amber-500/10 px-2 py-1.5 rounded-md border border-amber-500/20 animate-pulse-glow">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
              <TrendingUp className="h-3.5 w-3.5 text-amber-400" />
              <span className="text-amber-400 font-semibold text-sm">
                ${goldPrice.price_per_ounce.toLocaleString()}/oz
              </span>
              <span className="text-xs text-green-400">LIVE</span>
            </div>
          )}

          {/* Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <button onClick={() => scrollToSection('home')} className="nav-link text-gray-300 hover:text-amber-400 transition-colors">
              Home
            </button>
            <button onClick={() => scrollToSection('about')} className="nav-link text-gray-300 hover:text-amber-400 transition-colors">
              About
            </button>
            <button onClick={() => scrollToSection('mining-heritage')} className="nav-link text-gray-300 hover:text-amber-400 transition-colors">
              Mining Heritage
            </button>
            <button onClick={() => scrollToSection('security')} className="nav-link text-gray-300 hover:text-amber-400 transition-colors">
              Security
            </button>
            <button onClick={() => scrollToSection('leadership-team')} className="nav-link text-gray-300 hover:text-amber-400 transition-colors">
              Leadership Team
            </button>
            <button onClick={() => scrollToSection('ethical-sourcing')} className="nav-link text-gray-300 hover:text-amber-400 transition-colors">
              Ethical Sourcing
            </button>
            <button onClick={() => scrollToSection('kyc-aml')} className="nav-link text-gray-300 hover:text-amber-400 transition-colors">
              KYC/AML
            </button>
            <button onClick={() => scrollToSection('contact')} className="nav-link text-gray-300 hover:text-amber-400 transition-colors">
              Contact
            </button>
            <button onClick={() => scrollToSection('disclaimer')} className="nav-link text-gray-300 hover:text-amber-400 transition-colors">
              Disclaimer
            </button>
            {user ? (
              <div className="flex items-center space-x-3">
                <Link 
                  to="/dashboard" 
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold px-4 py-2 rounded-lg hover:from-amber-400 hover:to-yellow-400 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl border-2 border-amber-400 animate-pulse-glow flex items-center"
                >
                  <User className="h-5 w-5 mr-2" />
                  {user.is_ai_cfo ? '🤖 AI CFO Panel' : user.is_admin ? '🔧 Admin Panel' : '💼 My Dashboard'}
                </Link>
                <span className="text-gray-300">Welcome, {user.full_name}</span>
                <Button onClick={logout} variant="outline" size="sm">
                  Logout
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <LoginDialog />
                <RegisterDialog />
              </div>
            )}
          </nav>
          
          {/* Mobile Navigation Menu */}
          <div className="lg:hidden">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="border-amber-500 text-amber-400"
            >
              Menu
            </Button>
          </div>
        </div>
        
        {/* Mobile Navigation Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-amber-500/20">
            <div className="flex flex-col space-y-3 pt-4">
              <button onClick={() => { scrollToSection('home'); setMobileMenuOpen(false); }} className="text-left text-gray-300 hover:text-amber-400 transition-colors">
                Home
              </button>
              <button onClick={() => { scrollToSection('about'); setMobileMenuOpen(false); }} className="text-left text-gray-300 hover:text-amber-400 transition-colors">
                About
              </button>
              <button onClick={() => { scrollToSection('mining-heritage'); setMobileMenuOpen(false); }} className="text-left text-gray-300 hover:text-amber-400 transition-colors">
                Mining Heritage
              </button>
              <button onClick={() => { scrollToSection('security'); setMobileMenuOpen(false); }} className="text-left text-gray-300 hover:text-amber-400 transition-colors">
                Security
              </button>
              <button onClick={() => { scrollToSection('leadership-team'); setMobileMenuOpen(false); }} className="text-left text-gray-300 hover:text-amber-400 transition-colors">
                Leadership Team
              </button>
              <button onClick={() => { scrollToSection('ethical-sourcing'); setMobileMenuOpen(false); }} className="text-left text-gray-300 hover:text-amber-400 transition-colors">
                Ethical Sourcing
              </button>
              <button onClick={() => { scrollToSection('kyc-aml'); setMobileMenuOpen(false); }} className="text-left text-gray-300 hover:text-amber-400 transition-colors">
                KYC/AML
              </button>
              <button onClick={() => { scrollToSection('contact'); setMobileMenuOpen(false); }} className="text-left text-gray-300 hover:text-amber-400 transition-colors">
                Contact
              </button>
              <button onClick={() => { scrollToSection('disclaimer'); setMobileMenuOpen(false); }} className="text-left text-gray-300 hover:text-amber-400 transition-colors">
                Disclaimer
              </button>
              {user ? (
                <div className="flex flex-col space-y-2 pt-2 border-t border-amber-500/20">
                  <Link 
                    to="/dashboard" 
                    onClick={() => setMobileMenuOpen(false)} 
                    className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold px-4 py-3 rounded-lg text-center hover:from-amber-400 hover:to-yellow-400 transition-all duration-300 transform hover:scale-105 shadow-lg border-2 border-amber-400 flex items-center justify-center"
                  >
                    <User className="h-5 w-5 mr-2" />
                    {user.is_ai_cfo ? '🤖 AI CFO Panel' : user.is_admin ? '🔧 Admin Panel' : '💼 My Dashboard'}
                  </Link>
                  <span className="text-gray-300">Welcome, {user.full_name}</span>
                  <Button onClick={logout} variant="outline" size="sm" className="self-start">
                    Logout
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col space-y-2 pt-2 border-t border-amber-500/20">
                  <LoginDialog />
                  <RegisterDialog />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Login Dialog
const LoginDialog = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      login(response.data.token, response.data.user);
      setOpen(false);
      setEmail('');
      setPassword('');
      
      // Show verification message if needed
      if (response.data.requires_verification) {
        // This will be shown by the EmailVerificationBanner component
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen} modal={false}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">Login</Button>
        </DialogTrigger>
        <DialogContent className="bg-gray-900 border-amber-500/20 z-[60]">
          <DialogHeader>
            <DialogTitle className="text-white">Login to Your Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert className="border-red-500/20 bg-red-500/10">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-400">{error}</AlertDescription>
              </Alert>
            )}
            <div>
              <Label htmlFor="login-email" className="text-gray-300">Email</Label>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <Label htmlFor="login-password" className="text-gray-300">Password</Label>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-gray-800 border-gray-700 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-black">
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowPasswordReset(true)}
                className="text-sm text-amber-400 hover:text-amber-300 underline"
              >
                Forgot your password?
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      <PasswordResetDialog 
        open={showPasswordReset} 
        onClose={() => setShowPasswordReset(false)} 
      />
    </>
  );
};

// Register Dialog
const RegisterDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    full_name: '',
    phone: '',
    address: ''
  });
  const [error, setError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const { login } = useAuth();

  // Password validation function
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push("Must contain at least one uppercase letter");
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push("Must contain at least one lowercase letter");
    }
    
    if (!/[@#$%&*!.,?;:()[\]{}+\-=_/\\|~`]/.test(password)) {
      errors.push("Must contain at least one special character");
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPasswordErrors([]);

    // Validate passwords match
    if (formData.password !== formData.password_confirm) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Client-side password validation
    const errors = validatePassword(formData.password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      login(response.data.token, response.data.user);
      setOpen(false);
      setFormData({ 
        email: '', 
        password: '', 
        password_confirm: '', 
        full_name: '', 
        phone: '', 
        address: '' 
      });
      setPasswordErrors([]);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail.errors) {
        setPasswordErrors(errorDetail.errors);
        setError(errorDetail.message);
      } else {
        setError(errorDetail || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Real-time password validation
    if (name === 'password') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen} modal={false}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black">Register</Button>
      </DialogTrigger>
      <DialogContent className="bg-gray-900 border-amber-500/20 max-w-md z-[60] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Create Your Account</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert className="border-red-500/20 bg-red-500/10">
              <AlertCircle className="h-4 w-4 text-red-400" />
              <AlertDescription className="text-red-400">{error}</AlertDescription>
            </Alert>
          )}
          
          <div>
            <Label htmlFor="full_name" className="text-gray-300">Full Name</Label>
            <Input
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              required
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>
          
          <div>
            <Label htmlFor="email" className="text-gray-300">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>
          
          <div>
            <Label htmlFor="password" className="text-gray-300">
              Password
              <span className="text-xs text-gray-400 ml-2">
                (Min 8 chars, 1 uppercase, 1 lowercase, 1 special char)
              </span>
            </Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                required
                className="bg-gray-800 border-gray-700 text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordErrors.length > 0 && (
              <div className="mt-2 space-y-1">
                {passwordErrors.map((error, index) => (
                  <p key={index} className="text-xs text-red-400 flex items-center">
                    <AlertCircle size={12} className="mr-1" />
                    {error}
                  </p>
                ))}
              </div>
            )}
          </div>
          
          <div>
            <Label htmlFor="password_confirm" className="text-gray-300">Confirm Password</Label>
            <div className="relative">
              <Input
                id="password_confirm"
                name="password_confirm"
                type={showPasswordConfirm ? "text" : "password"}
                value={formData.password_confirm}
                onChange={handleChange}
                required
                className="bg-gray-800 border-gray-700 text-white pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPasswordConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {formData.password_confirm && formData.password !== formData.password_confirm && (
              <p className="text-xs text-red-400 mt-1 flex items-center">
                <AlertCircle size={12} className="mr-1" />
                Passwords do not match
              </p>
            )}
          </div>
          
          <div>
            <Label htmlFor="phone" className="text-gray-300">Phone (Optional)</Label>
            <Input
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>
          
          <div>
            <Label htmlFor="address" className="text-gray-300">Address (Optional)</Label>
            <Textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>
          
          <Button 
            type="submit" 
            disabled={loading || passwordErrors.length > 0 || formData.password !== formData.password_confirm} 
            className="w-full bg-amber-500 hover:bg-amber-600 text-black disabled:bg-gray-600 disabled:text-gray-400"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Email Verification Banner Component
const EmailVerificationBanner = ({ user, onResend }) => {
  const [resending, setResending] = useState(false);
  const [attempts, setAttempts] = useState(3);

  const handleResend = async () => {
    setResending(true);
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, { email: user.email });
      onResend(response.data.message);
      setAttempts(response.data.attempts_remaining || 0);
    } catch (error) {
      onResend(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  if (user?.email_verified) return null;

  return (
    <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-lg mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-amber-500 mr-3" />
          <div>
            <p className="text-amber-200 font-medium">Email Verification Required</p>
            <p className="text-amber-300 text-sm">
              Please check your email and click the verification link to access all features.
            </p>
          </div>
        </div>
        <Button
          onClick={handleResend}
          disabled={resending || attempts <= 0}
          variant="outline"
          size="sm"
          className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black"
        >
          {resending ? 'Sending...' : `Resend (${attempts} left)`}
        </Button>
      </div>
    </div>
  );
};

// Password Reset Dialog Component
const PasswordResetDialog = ({ open, onClose }) => {
  const [step, setStep] = useState('request'); // 'request' or 'reset'
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Get token from URL if present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      setStep('reset');
    }
  }, []);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push("Password must be at least 8 characters long");
    if (!/[A-Z]/.test(password)) errors.push("Must contain at least one uppercase letter");
    if (!/[a-z]/.test(password)) errors.push("Must contain at least one lowercase letter");
    if (!/[@#$%&*!.,?;:()[\]{}+\-=_/\\|~`]/.test(password)) errors.push("Must contain at least one special character");
    return errors;
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(`${API}/auth/request-password-reset`, { email });
      setMessage(response.data.message);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      setMessage(response.data.message);
      setTimeout(() => {
        onClose();
        setStep('request');
        setNewPassword('');
        setConfirmPassword('');
        setToken('');
      }, 2000);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail.errors) {
        setPasswordErrors(errorDetail.errors);
        setError(errorDetail.message);
      } else {
        setError(errorDetail || 'Password reset failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { value } = e.target;
    setNewPassword(value);
    const errors = validatePassword(value);
    setPasswordErrors(errors);
  };

  return (
    <Dialog open={open} onOpenChange={onClose} modal={false}>
      <DialogContent className="bg-gray-900 border-amber-500/20 max-w-md z-[60]">
        <DialogHeader>
          <DialogTitle className="text-white">
            {step === 'request' ? 'Reset Password' : 'Set New Password'}
          </DialogTitle>
        </DialogHeader>

        {step === 'request' ? (
          <form onSubmit={handleRequestReset} className="space-y-4">
            {error && (
              <Alert className="border-red-500/20 bg-red-500/10">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-400">{error}</AlertDescription>
              </Alert>
            )}
            {message && (
              <Alert className="border-green-500/20 bg-green-500/10">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <AlertDescription className="text-green-400">{message}</AlertDescription>
              </Alert>
            )}
            
            <div>
              <Label htmlFor="reset-email" className="text-gray-300">Email Address</Label>
              <Input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Enter your email address"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading} 
              className="w-full bg-amber-500 hover:bg-amber-600 text-black"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-4">
            {error && (
              <Alert className="border-red-500/20 bg-red-500/10">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-400">{error}</AlertDescription>
              </Alert>
            )}
            {message && (
              <Alert className="border-green-500/20 bg-green-500/10">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <AlertDescription className="text-green-400">{message}</AlertDescription>
              </Alert>
            )}
            
            <div>
              <Label htmlFor="new-password" className="text-gray-300">
                New Password
                <span className="text-xs text-gray-400 ml-2">
                  (Min 8 chars, 1 uppercase, 1 lowercase, 1 special char)
                </span>
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={handlePasswordChange}
                  required
                  className="bg-gray-800 border-gray-700 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {passwordErrors.length > 0 && (
                <div className="mt-2 space-y-1">
                  {passwordErrors.map((error, index) => (
                    <p key={index} className="text-xs text-red-400 flex items-center">
                      <AlertCircle size={12} className="mr-1" />
                      {error}
                    </p>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <Label htmlFor="confirm-password" className="text-gray-300">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-gray-800 border-gray-700 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-400 mt-1 flex items-center">
                  <AlertCircle size={12} className="mr-1" />
                  Passwords do not match
                </p>
              )}
            </div>
            
            <Button 
              type="submit" 
              disabled={loading || passwordErrors.length > 0 || newPassword !== confirmPassword} 
              className="w-full bg-amber-500 hover:bg-amber-600 text-black disabled:bg-gray-600 disabled:text-gray-400"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Home Page Component
const HomePage = () => {
  const [goldPrice, setGoldPrice] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(new Date());
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [photoModal, setPhotoModal] = useState({
    isOpen: false,
    imageSrc: '',
    imageAlt: ''
  });

  useEffect(() => {
    fetchGoldPrice();
    const priceInterval = setInterval(fetchGoldPrice, 3000); // Update every 3 seconds to match goldapi.io
    
    // Force re-render every second to update the "time since last update" display
    const displayInterval = setInterval(() => {
      setCurrentTime(new Date()); // This forces a re-render every second
    }, 1000);
    
    return () => {
      clearInterval(priceInterval);
      clearInterval(displayInterval);
    };
  }, []);

  const fetchGoldPrice = async () => {
    try {
      console.log('🔄 Fetching gold price...', new Date().toLocaleTimeString());
      // Add cache-busting parameter to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/gold/current-price?t=${timestamp}`);
      console.log('✅ Gold price fetched:', response.data);
      
      // Update the gold price data
      setGoldPrice(response.data);
      // Update the timestamp separately for live display
      setLastUpdateTime(new Date());
    } catch (error) {
      console.error('❌ Error fetching gold price:', error);
      console.error('API URL:', `${API}/gold/current-price`);
    }
  };

  const openPhotoModal = (imageSrc, imageAlt) => {
    setPhotoModal({
      isOpen: true,
      imageSrc,
      imageAlt
    });
  };

  const closePhotoModal = () => {
    setPhotoModal({
      isOpen: false,
      imageSrc: '',
      imageAlt: ''
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSubmitted(true);
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
    }
  };

  const handleContactChange = (e) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden hero-pattern">
        {/* Multi-layer Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_aurelia-gold/artifacts/6dfqwo7y_MIne%20pic%203.jpg')`
            }}
          />
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-35"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1718752773283-de1f92513671?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxnb2xkJTIwYnVsbGlvbnxlbnwwfHx8fDE3NTgyNTE0ODl8MA&ixlib=rb-4.1.0&q=85')`,
              backgroundBlendMode: 'overlay'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
          <div className="absolute inset-0 gold-texture-bg opacity-40" />
        </div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 floating-elements pointer-events-none" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <div className="mb-8 animate-fade-in-up">
            <div className="inline-flex items-center space-x-2 glass-premium px-8 py-4 rounded-full border border-amber-500/40 backdrop-blur-sm mb-8 animate-pulse-glow">
              <Crown className="h-6 w-6 text-amber-400 animate-bounce-gentle" />
              <span className="text-amber-300 font-bold text-lg">Premium Gold Bullion Trading</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black mb-6 md:mb-8 text-amber-500 animate-gradient-shift">
              Aurelia Prosperity
            </h1>
            
            <div className="glass-amber rounded-xl md:rounded-2xl p-4 md:p-8 mb-6 md:mb-8 max-w-4xl mx-auto animate-fade-in-delay">
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-amber-100 mb-3 md:mb-4 leading-relaxed font-semibold">
                Your trusted partner in precious metals.
              </p>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 mb-3 md:mb-4 leading-relaxed">
                LBMA certified gold bullion backed by over 30 years of mining expertise.
              </p>
              <div className="flex flex-wrap justify-center gap-2 md:gap-4 mt-3 md:mt-4 text-sm md:text-lg text-amber-200">
                <span className="glass px-3 md:px-4 py-1 md:py-2 rounded-full border border-amber-500/30">High-volume</span>
                <span className="glass px-3 md:px-4 py-1 md:py-2 rounded-full border border-amber-500/30">Large-quantity</span>
                <span className="glass px-3 md:px-4 py-1 md:py-2 rounded-full border border-amber-500/30">Ethical sourcing</span>
              </div>
            </div>
          </div>

          {/* Live Gold Price Display */}
          {goldPrice && (
            <div className="bg-gradient-to-r from-amber-500/10 to-yellow-600/10 backdrop-blur-md border border-amber-500/20 rounded-xl md:rounded-2xl p-4 md:p-8 mb-6 md:mb-8 max-w-sm md:max-w-md mx-auto animate-pulse-glow">
              <div className="flex items-center justify-center space-x-2 md:space-x-3 mb-3 md:mb-4">
                <TrendingUp className="h-4 w-4 md:h-6 md:w-6 text-amber-400 animate-bounce-gentle" />
                <span className="text-sm md:text-lg font-semibold text-amber-400">Live Gold Price</span>
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              </div>
              <div className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2 animate-fade-in-up">
                ${goldPrice.price_per_ounce.toLocaleString()}
              </div>
              <div className="text-xs md:text-sm text-gray-400">
                per troy ounce • Updated: {lastUpdateTime.toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles' })}
              </div>
              <div className="text-xs text-green-400 mt-1">
                ● LIVE via GoldAPI
              </div>
            </div>
          )}

          {/* Live Gold Price Ticker Bar */}
          {goldPrice && (
            <div className="mb-6 md:mb-8">
              <div className="glass-amber rounded-full px-6 py-3 max-w-2xl mx-auto animate-fade-in-delay">
                <div className="flex items-center justify-center space-x-4 text-sm md:text-base">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-amber-300 font-semibold">GOLD</span>
                  </div>
                  <div className="text-white font-bold">
                    ${goldPrice.price_per_ounce.toLocaleString()}
                  </div>
                  <div className="text-gray-300">USD/oz</div>
                  <div className="text-xs text-gray-400">
                    {lastUpdateTime.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <Button 
              size="lg" 
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6 md:px-8 py-3 text-base md:text-lg w-full sm:w-auto"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Get Quote Today
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-amber-500 text-amber-400 hover:bg-amber-500/10 px-6 md:px-8 py-3 text-base md:text-lg w-full sm:w-auto"
              onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-12 md:py-20 section-background-1 relative">
        <div className="absolute inset-0 gold-texture-bg opacity-20" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12 md:mb-16 animate-fade-in-up">
            <div className="glass-premium rounded-xl md:rounded-2xl p-6 md:p-8 max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 md:mb-8">
                About <span className="text-amber-500">Aurelia Prosperity</span>
              </h2>
              <p className="text-lg md:text-2xl text-amber-100 max-w-3xl mx-auto leading-relaxed">
                With over three decades of excellence in the mining industry, we bring unparalleled expertise to Mine Direct Precious Metals Acquisitions.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16 animate-fade-in-up">
            <div className="animate-slide-in-left">
              <div className="glass-premium rounded-2xl p-2 hover:scale-105 transition-transform duration-500">
                <img 
                  src="https://images.unsplash.com/photo-1647969476632-17261bc91afa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxnb2xkJTIwbWluaW5nJTIwb3BlcmF0aW9uc3xlbnwwfHx8fDE3NTgyNTE4Mjd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Mining Operations"
                  className="w-full h-80 object-cover rounded-xl"
                />
              </div>
            </div>
            
            <div className="space-y-8 animate-slide-in-right">
              <div className="flex items-start space-x-6 glass-amber rounded-2xl p-6 animate-scale-in hover:scale-105 transition-all duration-300">
                <div className="bg-amber-500/30 p-4 rounded-xl animate-bounce-gentle">
                  <Mountain className="h-8 w-8 text-amber-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">30+ Years Excellence</h3>
                  <p className="text-gray-200 text-lg">Three decades of expertise in mining operations and precious metals trading with unmatched industry knowledge.</p>
                </div>
              </div>

              <div className="flex items-start space-x-6 glass-amber rounded-2xl p-6 animate-scale-in hover:scale-105 transition-all duration-300" style={{animationDelay: '0.2s'}}>
                <div className="bg-amber-500/30 p-4 rounded-xl animate-bounce-gentle" style={{animationDelay: '0.5s'}}>
                  <Shield className="h-8 w-8 text-amber-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">LBMA Certified</h3>
                  <p className="text-gray-200 text-lg">All our gold bullion is certified by the London Bullion Market Association, ensuring authenticity and purity.</p>
                </div>
              </div>

              <div className="flex items-start space-x-6 glass-amber rounded-2xl p-6 animate-scale-in hover:scale-105 transition-all duration-300" style={{animationDelay: '0.4s'}}>
                <div className="bg-amber-500/30 p-4 rounded-xl animate-bounce-gentle" style={{animationDelay: '1s'}}>
                  <Award className="h-8 w-8 text-amber-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">Premium Quality</h3>
                  <p className="text-gray-200 text-lg">We source and sell only the highest quality gold bullion from trusted, ethical mines and refineries worldwide.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Heritage Section */}
          <div id="mining-heritage" className="grid md:grid-cols-2 gap-8 md:gap-12 items-center mb-16 md:mb-20 animate-fade-in-up scroll-mt-20">
            <div className="order-1 md:order-1 space-y-4 md:space-y-6 animate-slide-in-left">
              <h3 className="text-2xl md:text-3xl font-bold text-white">Our Mining Heritage</h3>
              <p className="text-gray-400 text-base md:text-lg leading-relaxed">
                Founded on the principles of integrity and excellence, Aurelia Prosperity began as a traditional mining operation 
                over three decades ago. Through years of hands-on experience in extraction, refining, and understanding the 
                intricate dynamics of gold production, we developed unparalleled expertise in precious metals.
              </p>
              <p className="text-gray-400 text-base md:text-lg leading-relaxed">
                Today, we have evolved this deep mining knowledge into our current business model: <span className="text-amber-400 font-semibold">high-volume, 
                large-quantity trading of gold bullion</span>. Our unique position in the market stems from our ability to understand 
                gold not just as a commodity, but as a product we've personally extracted, refined, and perfected.
              </p>
              <p className="text-gray-400 text-base md:text-lg leading-relaxed">
                Beyond sourcing, we specialize in <span className="text-amber-400 font-semibold">gold trading, offering a transparent and reliable supply chain</span> that connects mines directly to buyers worldwide. This seamless integration of <span className="text-amber-400 font-semibold">secure logistics, elite precious metals access, and trading expertise</span> makes us a partner of choice for producers, traders, and discerning investors alike.
              </p>
            </div>
            
            <div className="order-2 md:order-2 animate-slide-in-right">
              <div className="glass-premium rounded-xl md:rounded-2xl p-1 md:p-2 hover:scale-105 transition-transform duration-500 mb-4 md:mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aurelia-gold/artifacts/6dfqwo7y_MIne%20pic%203.jpg"
                  alt="Open Pit Mining Operations"
                  className="w-full h-48 md:h-64 object-cover rounded-lg md:rounded-xl"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div className="glass-premium rounded-lg md:rounded-2xl p-1 hover:scale-105 transition-transform duration-500">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_aurelia-gold/artifacts/2tbvc5kl_MIne%20pic%201.jpg"
                    alt="Underground Mining Operations"
                    className="w-full h-24 md:h-32 object-cover rounded-lg md:rounded-xl"
                  />
                </div>
                <div className="glass-premium rounded-lg md:rounded-2xl p-1 hover:scale-105 transition-transform duration-500">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_aurelia-gold/artifacts/djzecj9f_MIne%20pic%202.jpg"
                    alt="Mining Transport Operations"
                    className="w-full h-24 md:h-32 object-cover rounded-lg md:rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Gold Trading Capabilities Section */}
          <div className="mb-16 md:mb-20 relative section-background-2 rounded-3xl overflow-hidden animate-fade-in-up">
            <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-gray-900/90 to-black/85" />
            <div className="absolute inset-0 gold-texture-bg opacity-20" />
            
            <div className="relative p-8 md:p-16">
              <div className="text-center mb-12 md:mb-16">
                <div className="glass-premium rounded-2xl md:rounded-3xl p-8 md:p-12 max-w-5xl mx-auto">
                  <h3 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-6 md:mb-8">
                    Elite <span className="text-amber-500">Gold Trading</span> Operations
                  </h3>
                  <p className="text-amber-100 text-lg md:text-2xl max-w-4xl mx-auto leading-relaxed font-semibold">
                    Our trading desk handles <span className="text-amber-300 font-bold">high-volume, large-quantity transactions</span> with the precision and expertise that only comes from decades in the gold industry.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
                <div className="glass-premium rounded-xl md:rounded-2xl p-6 md:p-8 animate-scale-in hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20">
                  <div className="bg-gradient-to-br from-amber-500/30 to-yellow-600/30 w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6 animate-bounce-gentle">
                    <BarChart3 className="h-8 w-8 md:h-10 md:w-10 text-amber-200" />
                  </div>
                  <h4 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4 text-center">High-Volume Trading</h4>
                  <p className="text-gray-200 text-center leading-relaxed text-sm md:text-base">
                    Our trading desk specializes in <span className="text-amber-300 font-bold">large-scale transactions</span>, 
                    handling multi-million dollar gold trades with institutional-grade precision and speed.
                  </p>
                </div>

                <div className="glass-premium rounded-xl md:rounded-2xl p-6 md:p-8 animate-scale-in hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20" style={{animationDelay: '0.2s'}}>
                  <div className="bg-gradient-to-br from-amber-500/30 to-yellow-600/30 w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6 animate-bounce-gentle" style={{animationDelay: '0.5s'}}>
                    <TrendingUp className="h-8 w-8 md:h-10 md:w-10 text-amber-200" />
                  </div>
                  <h4 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4 text-center">Market Intelligence</h4>
                  <p className="text-gray-200 text-center leading-relaxed text-sm md:text-base">
                    Leveraging decades of market knowledge and real-time analytics to deliver 
                    <span className="text-amber-300 font-bold"> optimal pricing</span> and timing for every transaction.
                  </p>
                </div>

                <div className="glass-premium rounded-xl md:rounded-2xl p-6 md:p-8 animate-scale-in hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20 md:col-span-2 lg:col-span-1" style={{animationDelay: '0.4s'}}>
                  <div className="bg-gradient-to-br from-amber-500/30 to-yellow-600/30 w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6 animate-bounce-gentle" style={{animationDelay: '1s'}}>
                    <Coins className="h-8 w-8 md:h-10 md:w-10 text-amber-200" />
                  </div>
                  <h4 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4 text-center">Institutional Grade</h4>
                  <p className="text-gray-200 text-center leading-relaxed text-sm md:text-base">
                    Our trading infrastructure supports <span className="text-amber-300 font-bold">institutional-level clients</span>, 
                    from private wealth managers to sovereign funds and central banks.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                <div className="space-y-6 md:space-y-8 animate-slide-in-left">
                  <h4 className="text-2xl md:text-3xl font-bold text-white">Trading Excellence That Sets Us Apart</h4>
                  
                  <div className="space-y-4 md:space-y-6">
                    <div className="glass-amber rounded-xl p-4 md:p-6 flex items-start space-x-4">
                      <div className="bg-amber-500/30 p-2 md:p-3 rounded-lg min-w-fit">
                        <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-amber-300" />
                      </div>
                      <div>
                        <h5 className="text-white font-bold mb-2 text-lg md:text-xl">Real-Time Execution</h5>
                        <p className="text-gray-200 text-sm md:text-base">Lightning-fast trade execution with live market pricing and instant settlement capabilities for time-sensitive opportunities.</p>
                      </div>
                    </div>

                    <div className="glass-amber rounded-xl p-4 md:p-6 flex items-start space-x-4">
                      <div className="bg-amber-500/30 p-2 md:p-3 rounded-lg min-w-fit">
                        <Shield className="h-5 w-5 md:h-6 md:w-6 text-amber-300" />
                      </div>
                      <div>
                        <h5 className="text-white font-bold mb-2 text-lg md:text-xl">Risk Management</h5>
                        <p className="text-gray-200 text-sm md:text-base">Advanced risk assessment and hedging strategies to protect client investments across volatile market conditions.</p>
                      </div>
                    </div>

                    <div className="glass-amber rounded-xl p-4 md:p-6 flex items-start space-x-4">
                      <div className="bg-amber-500/30 p-2 md:p-3 rounded-lg min-w-fit">
                        <Award className="h-5 w-5 md:h-6 md:w-6 text-amber-300" />
                      </div>
                      <div>
                        <h5 className="text-white font-bold mb-2 text-lg md:text-xl">Global Network Access</h5>
                        <p className="text-gray-200 text-sm md:text-base">Direct access to major bullion markets in London, Dubai, New York, and Hong Kong through our established partnerships.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="animate-slide-in-right">
                  <div className="glass-premium rounded-2xl md:rounded-3xl p-6 md:p-8 text-center">
                    <div className="mb-6 md:mb-8">
                      <div className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-amber-500/40 to-yellow-600/40 rounded-2xl mb-4 md:mb-6 animate-pulse-glow">
                        <TrendingUp className="h-10 w-10 md:h-12 md:w-12 text-amber-300" />
                      </div>
                      <h4 className="text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4">Trading Volume</h4>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-4 md:gap-6">
                      <div className="bg-gradient-to-r from-amber-500/20 to-yellow-600/20 rounded-xl p-4 md:p-6 border border-amber-500/30">
                        <div className="text-2xl md:text-3xl font-black text-amber-300 mb-1 md:mb-2">$500M+</div>
                        <div className="text-gray-300 text-sm md:text-base">Annual Trading Volume</div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-amber-500/20 to-yellow-600/20 rounded-xl p-4 md:p-6 border border-amber-500/30">
                        <div className="text-2xl md:text-3xl font-black text-amber-300 mb-1 md:mb-2">15,000+</div>
                        <div className="text-gray-300 text-sm md:text-base">Troy Ounces Monthly</div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-amber-500/20 to-yellow-600/20 rounded-xl p-4 md:p-6 border border-amber-500/30">
                        <div className="text-2xl md:text-3xl font-black text-amber-300 mb-1 md:mb-2">24/7</div>
                        <div className="text-gray-300 text-sm md:text-base">Market Monitoring</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 md:mt-16 p-6 md:p-8 bg-gradient-to-r from-amber-500/20 to-yellow-600/20 rounded-2xl md:rounded-3xl border border-amber-500/30 animate-fade-in-up">
                <div className="text-center">
                  <h5 className="text-2xl md:text-3xl font-bold text-white mb-4 md:mb-6">
                    From Mine to Market: Unmatched Trading Expertise
                  </h5>
                  <p className="text-amber-200 text-lg md:text-xl leading-relaxed max-w-5xl mx-auto">
                    Our unique combination of <span className="text-amber-300 font-bold">mining heritage and trading excellence</span> allows us to offer competitive pricing, guaranteed supply, and unparalleled market insights. Whether you're 
                    looking to diversify your portfolio or make large-scale gold investments, our trading desk delivers results 
                    that exceed expectations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Mining Network & Sourcing Advantage Section */}
          <div className="mb-20 relative">
            <div className="glass-premium rounded-3xl p-12 animate-fade-in-up">
              <div className="text-center mb-12">
                <h3 className="text-4xl font-bold text-white mb-6">
                  <span className="text-amber-500">Strategic Mining Networks</span> & Sourcing Excellence
                </h3>
                <p className="text-xl text-amber-100 max-w-4xl mx-auto leading-relaxed">
                  Our decades of hands-on mining experience have cultivated an extensive network of trusted mining partnerships worldwide
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <h4 className="text-2xl font-bold text-white">Unparalleled Mining Relationships</h4>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    Through our 30+ years in the mining industry, we have forged deep, trusted relationships with mining operations 
                    across multiple continents. These partnerships, built on mutual respect and shared expertise, provide us with 
                    privileged access to high-quality gold sources that most trading firms simply cannot reach.
                  </p>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    Our mining heritage means we speak the same language as producers - we understand their challenges, operational 
                    cycles, and quality standards. This insider knowledge allows us to negotiate favorable terms and secure consistent 
                    supply chains that feed directly into our high-volume trading operations.
                  </p>
                </div>

                <div className="space-y-6">
                  <h4 className="text-2xl font-bold text-white">Competitive Trading Advantage</h4>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    While traditional gold traders rely on intermediaries and market speculation, our mining relationships give us 
                    <span className="text-amber-400 font-semibold"> direct access to source</span>. This unique positioning enables 
                    us to offer our clients superior pricing, guaranteed supply, and unmatched transparency in every transaction.
                  </p>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="glass-amber rounded-xl p-4 flex items-center space-x-4">
                      <div className="bg-amber-500/30 p-2 rounded-lg">
                        <CheckCircle className="h-6 w-6 text-amber-300" />
                      </div>
                      <div>
                        <h5 className="text-white font-semibold">Direct Mine-to-Market Access</h5>
                        <p className="text-gray-300 text-sm">Bypassing traditional supply chain intermediaries</p>
                      </div>
                    </div>
                    
                    <div className="glass-amber rounded-xl p-4 flex items-center space-x-4">
                      <div className="bg-amber-500/30 p-2 rounded-lg">
                        <Shield className="h-6 w-6 text-amber-300" />
                      </div>
                      <div>
                        <h5 className="text-white font-semibold">Preferential Sourcing Terms</h5>
                        <p className="text-gray-300 text-sm">Exclusive access to premium gold at competitive rates</p>
                      </div>
                    </div>
                    
                    <div className="glass-amber rounded-xl p-4 flex items-center space-x-4">
                      <div className="bg-amber-500/30 p-2 rounded-lg">
                        <TrendingUp className="h-6 w-6 text-amber-300" />
                      </div>
                      <div>
                        <h5 className="text-white font-semibold">High-Volume Supply Guarantee</h5>
                        <p className="text-gray-300 text-sm">Reliable inventory to support large-scale trading operations</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-8 bg-gradient-to-r from-amber-500/20 to-yellow-600/20 rounded-2xl border border-amber-500/30">
                <div className="text-center">
                  <h5 className="text-2xl font-bold text-white mb-4">
                    From Mine to Market: A Seamless Supply Chain
                  </h5>
                  <p className="text-amber-200 text-lg leading-relaxed max-w-4xl mx-auto">
                    Our established mining network doesn't just provide us with gold - it gives us market intelligence, 
                    early access to new discoveries, and the ability to secure long-term supply contracts that ensure 
                    our high-volume trading desk never runs short of premium inventory. This operational advantage 
                    translates directly into better outcomes for our clients.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Elite Security Section */}
          <div id="security" className="mb-16 md:mb-20 relative section-background-3 rounded-3xl overflow-hidden scroll-mt-20">
            {/* Enhanced Background Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-gray-900/95 to-black/90" />
            <div className="absolute inset-0 gold-texture-bg opacity-15" />
            
            <div className="relative p-12 lg:p-16">
              <div className="text-center mb-16 animate-fade-in-up">
                <div className="glass-premium rounded-3xl p-12 max-w-5xl mx-auto">
                  <h3 className="text-5xl md:text-6xl font-black text-white mb-8">
                    <span className="block sm:inline">High Tech</span> <span className="text-amber-500">Security</span>
                  </h3>
                  <p className="text-amber-100 text-2xl md:text-3xl max-w-4xl mx-auto leading-relaxed font-semibold">
                    What sets us apart is our <span className="text-amber-300 font-bold">high tech focus on security</span>. 
                    From <span className="text-amber-300 font-bold">mine to bank</span>, every step of the journey is protected by 
                    high-end security protocols, ensuring the safe handling, transport, and delivery of gold.
                  </p>
                  <p className="text-gray-200 text-xl mt-6">
                    Our clients — including <span className="text-amber-300 font-bold">ultra high-net-worth investors</span> — trust us with their most valuable assets.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                <div className="glass-premium rounded-2xl p-8 animate-scale-in hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20">
                  <div className="bg-gradient-to-br from-amber-500/30 to-yellow-600/30 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-bounce-gentle">
                    <Shield className="h-10 w-10 text-amber-200" />
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4 text-center">Elite Security Team</h4>
                  <p className="text-gray-200 text-center leading-relaxed">
                    <span className="text-amber-300 font-bold text-lg">5 seasoned professionals</span> with over 30 years combined experience 
                    in precious metals security, utilizing the most advanced technology for uncompromised protection.
                  </p>
                </div>

                <div className="glass-premium rounded-2xl p-8 animate-scale-in hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20" style={{animationDelay: '0.2s'}}>
                  <div className="bg-gradient-to-br from-amber-500/30 to-yellow-600/30 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-bounce-gentle" style={{animationDelay: '0.5s'}}>
                    <Truck className="h-10 w-10 text-amber-200" />
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4 text-center">Secure Transportation</h4>
                  <p className="text-gray-200 text-center leading-relaxed">
                    Military-grade armored transport with GPS tracking, real-time monitoring, and armed escort services 
                    from <span className="text-amber-300 font-bold">mine to refinery to bank</span> delivery.
                  </p>
                </div>

                <div className="glass-premium rounded-2xl p-8 animate-scale-in hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20" style={{animationDelay: '0.4s'}}>
                  <div className="bg-gradient-to-br from-amber-500/30 to-yellow-600/30 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-bounce-gentle" style={{animationDelay: '1s'}}>
                    <Award className="h-10 w-10 text-amber-200" />
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4 text-center">Advanced Technology</h4>
                  <p className="text-gray-200 text-center leading-relaxed">
                    Biometric access controls, encrypted communications, satellite surveillance, and AI-powered threat detection 
                    systems protect every stage of the gold handling process.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="space-y-6 animate-slide-in-left">
                  <h4 className="text-2xl font-bold text-white">Mine-to-Bank Security Protocol</h4>
                  
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="bg-amber-500/20 p-2 rounded-lg min-w-fit">
                        <Mountain className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <h5 className="text-white font-semibold mb-1">Mining Site Security</h5>
                        <p className="text-gray-400 text-sm">24/7 perimeter monitoring, access control systems, and on-site security personnel.</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="bg-amber-500/20 p-2 rounded-lg min-w-fit">
                        <Package className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <h5 className="text-white font-semibold mb-1">Secure Processing</h5>
                        <p className="text-gray-400 text-sm">Fully monitored refinery operations with tamper-proof containers and chain of custody documentation.</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="bg-amber-500/20 p-2 rounded-lg min-w-fit">
                        <CheckCircle className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <h5 className="text-white font-semibold mb-1">Bank-Grade Delivery</h5>
                        <p className="text-gray-400 text-sm">Direct delivery to approved banking institutions with full insurance coverage and verified receipt protocols.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="animate-slide-in-right">
                  <div className="bg-gradient-to-r from-amber-500/10 to-yellow-600/10 rounded-2xl p-1">
                    <img 
                      src="https://images.unsplash.com/photo-1637252166739-b47f8875f304?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxhcm1vcmVkJTIwc2VjdXJpdHklMjB0cmFuc3BvcnR8ZW58MHx8fHwxNzU4MzM2NDgwfDA&ixlib=rb-4.1.0&q=85"
                      alt="Professional Armored Security Transport Vehicle"
                      className="w-full h-80 object-cover rounded-2xl hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="mt-4 text-center">
                    <p className="text-amber-400 text-sm font-semibold">Professional Armored Security Transport</p>
                    <p className="text-gray-400 text-xs">Military-grade protection for precious metals transportation</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-gradient-to-r from-amber-500/20 to-yellow-600/20 rounded-xl border border-amber-500/30 animate-fade-in-up">
                <div className="text-center">
                  <h5 className="text-xl font-bold text-white mb-3">
                    Trusted by Ultra High-Net-Worth Investors Worldwide
                  </h5>
                  <p className="text-amber-200 text-lg leading-relaxed">
                    Our unparalleled security infrastructure and transparent operations have earned the trust of institutional 
                    investors, private wealth managers, and discerning clients who demand the highest standards of protection 
                    for their precious metals investments.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div id="leadership-team" className="mb-16 md:mb-20 relative scroll-mt-20">
            {/* Background Image */}
            <div 
              className="absolute inset-0 opacity-5 bg-cover bg-center rounded-xl md:rounded-2xl"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1746037870491-b2e415517d0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHx0cmFkaW5nJTIwZmxvb3J8ZW58MHx8fHwxNzU4MjU4NDI2fDA&ixlib=rb-4.1.0&q=85')`
              }}
            />
            
            <div className="relative text-center mb-8 md:mb-12 animate-fade-in-up">
              <h3 className="text-2xl sm:text-3xl md:text-3xl font-bold text-white mb-3 md:mb-4">Leadership Team</h3>
              <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
                Our experienced leadership team brings decades of expertise in mining, technology, and global bullion trading.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 relative">
              <Card className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 border-amber-500/20 animate-scale-in hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/10">
                <CardContent className="p-4 md:p-6 text-center">
                  <div 
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full mx-auto mb-3 md:mb-4 overflow-hidden border-2 border-amber-500/30 cursor-pointer hover:border-amber-400 transition-colors duration-300 hover:scale-110 transform"
                    onClick={() => openPhotoModal(
                      "https://customer-assets.emergentagent.com/job_gold-trading-hub-3/artifacts/4pnp1v1x_Dean%20New%20headshot.jpg", 
                      "Dean Tate - Managing Director"
                    )}
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_gold-trading-hub-3/artifacts/4pnp1v1x_Dean%20New%20headshot.jpg" 
                      alt="Dean Tate - Managing Director" 
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-white mb-1 md:mb-2">Dean Tate</h4>
                  <p className="text-amber-400 font-semibold mb-2 md:mb-3 text-sm md:text-base">Managing Director</p>
                  <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                    With over 30 years of experience in the precious metals industry, Dean has built extensive expertise in both trading and mining, 
                    developing a global network that spans from major producers to high-net-worth private investors. He has managed 
                    multi-billion dollar portfolios and pioneered risk management protocols that became industry standards. 
                    Aurelia Prosperity has facilitated transactions in over 20 countries, establishing strategic partnerships 
                    with Tier-1 mining operations in Australia, Canada, and South Africa. His expertise in regulatory compliance spans AUSTRAC, 
                    CFTC, and international AML frameworks, ensuring seamless operations across multiple jurisdictions while maintaining the highest 
                    levels of security and compliance throughout every stage of the process.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 border-amber-500/20 animate-scale-in hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/10" style={{animationDelay: '0.2s'}}>
                <CardContent className="p-4 md:p-6 text-center">
                  <div 
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full mx-auto mb-3 md:mb-4 overflow-hidden border-2 border-amber-500/30 cursor-pointer hover:border-amber-400 transition-colors duration-300 hover:scale-110 transform"
                    onClick={() => openPhotoModal(
                      "https://customer-assets.emergentagent.com/job_gold-trading-hub-3/artifacts/iu01mtmi_047-NKm5gNhDD8U.jpeg", 
                      "Jesse Crawford - Chief Technology Officer"
                    )}
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_gold-trading-hub-3/artifacts/iu01mtmi_047-NKm5gNhDD8U.jpeg" 
                      alt="Jesse Crawford - Chief Technology Officer" 
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-white mb-1 md:mb-2">Jesse Crawford</h4>
                  <p className="text-amber-400 font-semibold mb-2 md:mb-3 text-sm md:text-base">Chief Technology Officer</p>
                  <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                    With over 15 years of experience in fintech and blockchain technology, Jesse architected our proprietary 
                    gold trading platforms capable of processing millions of dollars in transactions with microsecond latency. 
                    He leads our cybersecurity initiatives, implements advanced encryption protocols, and ensures 24/7 system 
                    reliability for our global operations. Jesse's expertise in distributed systems and API integrations enables 
                    real-time gold price feeds, automated compliance reporting, and seamless client portal experiences across 
                    multiple time zones and regulatory environments.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 border-amber-500/20 animate-scale-in hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/10" style={{animationDelay: '0.4s'}}>
                <CardContent className="p-4 md:p-6 text-center">
                  <div 
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full mx-auto mb-3 md:mb-4 overflow-hidden border-2 border-amber-500/30 cursor-pointer hover:border-amber-400 transition-colors duration-300 hover:scale-110 transform"
                    onClick={() => openPhotoModal(
                      "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=face&fit=crop&w=600&h=600&q=85", 
                      "Goldi - AI Chief Financial Officer"
                    )}
                  >
                    <img 
                      src="https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=face&fit=crop&w=400&h=400&q=85" 
                      alt="Goldi - AI Chief Financial Officer" 
                      className="w-full h-full object-cover object-center hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-white mb-1 md:mb-2">Goldi</h4>
                  <p className="text-amber-400 font-semibold mb-2 md:mb-3 text-sm md:text-base">AI Chief Financial Officer</p>
                  <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                    Cutting-edge AI Chief Financial Officer with advanced machine learning capabilities in precious metals markets. 
                    Specializes in real-time P&L optimization, risk management, and regulatory compliance for high-volume gold trading. 
                    Processes complex financial data at lightning speed, identifies profit opportunities, and provides 
                    comprehensive financial insights with 99.9% accuracy. Expert in cost analysis, market volatility assessment, 
                    and automated financial reporting for executive decision-making.
                  </p>
                </CardContent>
              </Card>

              {/* George Halikiotis removed as requested */}

              <Card className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 border-amber-500/20 animate-scale-in hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/10" style={{animationDelay: '0.8s'}}>
                <CardContent className="p-4 md:p-6 text-center">
                  <div 
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full mx-auto mb-3 md:mb-4 overflow-hidden border-2 border-amber-500/30 cursor-pointer hover:border-amber-400 transition-colors duration-300 hover:scale-110 transform bg-gradient-to-b from-gray-700 to-gray-800 flex items-center justify-center"
                    onClick={() => openPhotoModal(
                      null, 
                      "Kenneth WHL - Investment Strategist"
                    )}
                  >
                    {/* Placeholder for Kenneth WHL photo */}
                    <div className="text-center">
                      <User className="h-12 w-12 md:h-16 md:w-16 text-amber-400/60 mx-auto mb-2" />
                      <p className="text-xs text-amber-400/60 font-medium">Photo Coming Soon</p>
                    </div>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-white mb-1 md:mb-2">Kenneth Whl</h4>
                  <p className="text-amber-400 font-semibold mb-2 md:mb-3 text-sm md:text-base">Investment Strategist</p>
                  <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                    Kenneth has combined Mergers & Acquisitions, financial, real estate asset & funds management, corporate advisory, capital raising disciplines in a successful career in Australia and South East Asia with private, publicly listed and foreign companies over the last 25 years. Kenneth had a tenure with Lehman Brothers Private Equity US$20 billion Global Fund in Debt & Equity as Portfolio Manager VP at their Singapore office, across multiple tax jurisdictions - prior, during and post the GFC. Through exposure to industries including precious metals, renewable energy, IT&T, real estate, finance, asset & funds management and real estate development with other entities such as AMP Capital, Lendlease, Jones Lang LaSalle, Investa Property Group, Taragon Asia Pacific, StatusInvesta, Stratus EnergyX, and MacarthurCook covering the Australian, Singaporean and South East Asian markets with total transaction of US$50 billion Kenneth has developed an acutely strategic view toward business management, investment & development, portfolio performance, collaborative partnerships, client and stakeholder engagement.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Ethical Sourcing Section - Enhanced */}
          <div id="ethical-sourcing" className="grid md:grid-cols-2 gap-12 items-center mb-20 animate-fade-in-up scroll-mt-20">            
            <div className="space-y-6 animate-slide-in-left">
              <h3 className="text-3xl font-bold text-white">Ethical Sourcing & Global Supply Chain</h3>
              <p className="text-gray-400 text-lg leading-relaxed">
                Our three decades in the mining industry have established strong, ethical relationships with gold miners 
                worldwide. We maintain strict standards for responsible mining practices and ensure all our gold is 
                sourced through legitimate, environmentally conscious operations.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                This direct access to miners allows us to bypass traditional intermediaries, offering our clients 
                better pricing while maintaining complete transparency in our supply chain. Every piece of gold we 
                trade can be traced back to its ethical source, connecting <span className="text-amber-400 font-semibold">mines directly to buyers worldwide</span>.
              </p>
              
              <div className="flex items-start space-x-4 animate-scale-in">
                <div className="bg-amber-500/20 p-3 rounded-lg animate-bounce-gentle">
                  <Shield className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">Responsible Mining Partnerships</h4>
                  <p className="text-gray-400">Direct relationships with certified, ethical mining operations worldwide.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 animate-scale-in" style={{animationDelay: '0.2s'}}>
                <div className="bg-amber-500/20 p-3 rounded-lg animate-bounce-gentle" style={{animationDelay: '0.5s'}}>
                  <CheckCircle className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">Transparent Supply Chain</h4>
                  <p className="text-gray-400">Complete traceability from mine to market for every gold transaction with full documentation.</p>
                </div>
              </div>
            </div>

            <div className="animate-slide-in-right">
              <div className="bg-gradient-to-r from-amber-500/10 to-yellow-600/10 rounded-2xl p-1 mb-8">
                <img 
                  src="https://images.unsplash.com/photo-1647607223701-52bb084d3953?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYnVsbGlvbiUyMHN0b3JhZ2V8ZW58MHx8fHwxNzU4MjU4Mzg4fDA&ixlib=rb-4.1.0&q=85"
                  alt="Professional Gold Storage"
                  className="w-full h-80 object-cover rounded-2xl hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>

          {/* KYC/AML Compliance Section */}
          <div id="kyc-aml" className="bg-gradient-to-r from-gray-800/30 to-gray-900/30 rounded-2xl p-6 md:p-8 border border-amber-500/20 scroll-mt-20">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-white mb-4">KYC/AML Compliance</h3>
              <p className="text-gray-400 text-lg max-w-3xl mx-auto">
                Aurelia Prosperity Gold operates under strict compliance with Australian Know Your Customer (KYC) 
                and Anti-Money Laundering (AML) regulations to ensure the highest standards of financial integrity.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-amber-500/20 p-3 rounded-lg">
                    <Shield className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Australian AML/CTF Compliance</h4>
                    <p className="text-gray-400 text-sm">
                      We adhere to the Anti-Money Laundering and Counter-Terrorism Financing Act 2006 (AML/CTF Act) 
                      and maintain comprehensive compliance programs as required by AUSTRAC.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-amber-500/20 p-3 rounded-lg">
                    <User className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Enhanced Due Diligence</h4>
                    <p className="text-gray-400 text-sm">
                      All clients undergo thorough identity verification and enhanced due diligence procedures 
                      for high-value transactions, ensuring compliance with Australian financial services regulations.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-amber-500/20 p-3 rounded-lg">
                    <BarChart3 className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Transaction Monitoring</h4>
                    <p className="text-gray-400 text-sm">
                      Advanced monitoring systems track all transactions for suspicious activities, with mandatory 
                      reporting to relevant authorities as required under Australian law.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-amber-500/20 p-3 rounded-lg">
                    <Award className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Regulatory Compliance</h4>
                    <p className="text-gray-400 text-sm">
                      Regular audits and compliance reviews ensure our operations meet or exceed all Australian 
                      financial services and precious metals trading requirements.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <p className="text-amber-200 text-sm text-center">
                <strong>Compliance Notice:</strong> All transactions above AUD $10,000 are subject to enhanced 
                verification procedures in accordance with Australian AML/CTF regulations. We maintain strict 
                confidentiality while ensuring full regulatory compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 section-background-2 relative">
        <div className="absolute inset-0 gold-texture-bg opacity-25" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 animate-fade-in-up">
            <div className="glass-premium rounded-3xl p-12 max-w-4xl mx-auto">
              <h2 className="text-5xl md:text-6xl font-black text-white mb-8">
                Contact <span className="text-amber-500">Us</span>
              </h2>
              <p className="text-2xl text-amber-100 max-w-3xl mx-auto leading-relaxed">
                Ready to invest in gold? Get in touch with our experts for personalized guidance and competitive prices.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="bg-gradient-to-r from-amber-500/10 to-yellow-600/10 rounded-2xl p-1 mb-8">
                <img 
                  src="https://images.pexels.com/photos/789822/pexels-photo-789822.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Customer Service"
                  className="w-full h-64 object-cover rounded-2xl"
                />
              </div>

              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-amber-500/20 p-3 rounded-lg">
                    <Mail className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Email</h3>
                    <p className="text-gray-400">info@aureliaprosperity.gold</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="bg-amber-500/20 p-3 rounded-lg">
                    <MapPin className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Address</h3>
                    <p className="text-gray-400">50 Pitt Street<br />Sydney 2000 NSW, Australia</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="bg-gray-900/50 border-amber-500/20">
              <CardHeader>
                <CardTitle className="text-white">Get a Quote</CardTitle>
              </CardHeader>
              <CardContent>
                {contactSubmitted ? (
                  <Alert className="border-green-500/20 bg-green-500/10">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <AlertDescription className="text-green-400">
                      Thank you for your inquiry! We've received your message and our team will get back to you within 24 hours with detailed information about our gold bullion offerings.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-gray-300">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        value={contactForm.name}
                        onChange={handleContactChange}
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-300">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={contactForm.email}
                        onChange={handleContactChange}
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-gray-300">Phone (Optional)</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={contactForm.phone}
                        onChange={handleContactChange}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-gray-300">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={contactForm.message}
                        onChange={handleContactChange}
                        required
                        className="bg-gray-800 border-gray-700 text-white min-h-[100px]"
                        placeholder="Tell us about your investment goals and how much gold you're interested in purchasing..."
                      />
                    </div>
                    <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-black">
                      Send Message
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section id="disclaimer" className="py-16 bg-gradient-to-b from-gray-900 to-black border-t border-amber-500/10 relative">
        <div className="absolute inset-0 opacity-5 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1444653614773-995cb1ef9efa?crop=entropy&cs=srgb&fm=jpg&q=85')`
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                <Shield className="h-8 w-8 inline-block mr-3 text-amber-500" />
                Important Legal Disclaimer
              </h2>
              <p className="text-amber-200 text-lg">
                Please read this disclaimer carefully before using our services
              </p>
            </div>

            <Card className="bg-gray-900/80 border-amber-500/20 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="space-y-6 text-gray-300 leading-relaxed">
                  <div className="bg-amber-500/10 border-l-4 border-amber-500 p-4 rounded-r-lg">
                    <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-amber-500" />
                      Disclaimer
                    </h3>
                  </div>

                  <p className="text-sm md:text-base">
                    The information contained on{' '}
                    <span className="text-amber-400 font-medium">https://aureliaprosperity.gold</span>{' '}
                    (the "Website") is provided for general informational purposes only. Nothing on this Website is intended to constitute, and shall not be construed as constituting, an offer, solicitation, or recommendation to acquire, dispose of, or otherwise deal in any financial product or service.
                  </p>

                  <div className="bg-red-500/10 border-l-4 border-red-500 p-4 rounded-r-lg">
                    <p className="text-sm md:text-base">
                      <strong className="text-red-400">Important Notice:</strong> Aurelia Prosperity does not hold an Australian Financial Services Licence ("AFSL") or any other licence authorising it to trade in gold, precious metals, or related financial products. Where applicable, facilitation of transactions may be conducted through third-party entities duly authorised and licensed under the relevant laws and regulations.
                    </p>
                  </div>

                  <p className="text-sm md:text-base">
                    References on this Website to certain products, services, or solutions may relate to offerings that are in development, not yet available to the public, or otherwise subject to ongoing regulatory processes. Any statement regarding future availability, expected features, potential outcomes, or anticipated benefits constitutes a forward-looking statement. Such statements are inherently uncertain, are subject to regulatory approval in Australia, and may be influenced by risks, legal constraints, market fluctuations, and factors outside of Aurelia Prosperity's control. Actual results or availability of products and services may differ materially from those expressed or implied.
                  </p>

                  <div className="bg-yellow-500/10 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                    <p className="text-sm md:text-base">
                      <strong className="text-yellow-400">Liability Disclaimer:</strong> Aurelia Prosperity makes no representation or warranty, express or implied, as to the accuracy, reliability, or completeness of any information provided on this Website, including forward-looking statements. To the maximum extent permitted by law, Aurelia Prosperity disclaims all liability for any loss, damage, cost, or expense incurred by any person in reliance on the information presented.
                    </p>
                  </div>

                  <p className="text-sm md:text-base">
                    Users are strongly encouraged to conduct their own independent inquiries and, where necessary, obtain professional financial, legal, or other relevant advice prior to engaging in any transaction involving precious metals or related products.
                  </p>

                  <div className="bg-blue-500/10 border-l-4 border-blue-500 p-4 rounded-r-lg">
                    <p className="text-sm md:text-base font-medium text-blue-400">
                      By accessing or using this Website, you acknowledge and agree to the terms of this Disclaimer.
                    </p>
                  </div>

                  <div className="text-center pt-6 border-t border-gray-700">
                    <p className="text-xs text-gray-400">
                      Last Updated: December 2024 | For questions regarding this disclaimer, please contact{' '}
                      <span className="text-amber-400">info@aureliaprosperity.gold</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-black border-t border-amber-500/20">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="bg-gradient-to-r from-amber-400 to-yellow-600 p-2 rounded-lg">
                <Crown className="h-5 w-5 text-black" />
              </div>
              <span className="text-lg font-bold text-white">Aurelia Prosperity Gold</span>
            </div>
            <p className="text-gray-400 text-sm">
              © 2019-2025 Aurelia Prosperity Gold. All rights reserved.
            </p>
            <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 mt-2 mb-2">
              <Link to="/terms" className="hover:text-amber-400 transition-colors underline">
                Terms of Use
              </Link>
              <span>•</span>
              <Link to="/privacy" className="hover:text-amber-400 transition-colors underline">
                Privacy Policy
              </Link>
              <span>•</span>
              <button onClick={() => document.getElementById('disclaimer').scrollIntoView({ behavior: 'smooth' })} className="hover:text-amber-400 transition-colors underline">
                Disclaimer
              </button>
            </div>
            <p className="text-gray-500 text-xs">
              Professional gold bullion trading • LBMA Certified • 30+ Years Mining Excellence
            </p>
          </div>
        </div>
      </footer>

      {/* Photo Modal */}
      {photoModal.isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm"
          onClick={closePhotoModal}
        >
          <div className="relative max-w-2xl max-h-screen p-4">
            <div 
              className="relative bg-gray-900 rounded-lg overflow-hidden border-2 border-amber-500/30 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={photoModal.imageSrc}
                alt={photoModal.imageAlt}
                className="w-full h-auto max-h-[70vh] object-contain cursor-pointer"
                onClick={closePhotoModal}
              />
              <div className="absolute top-4 right-4">
                <button
                  onClick={closePhotoModal}
                  className="bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-70 transition-opacity"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="bg-gradient-to-t from-black to-transparent absolute bottom-0 left-0 right-0 p-6">
                {photoModal.imageAlt === "Goldi - AI Chief Financial Officer" ? (
                  <div className="text-center space-y-2">
                    <div className="flex items-center justify-center space-x-2 mb-3">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-green-400 text-sm font-semibold">AI SYSTEM ACTIVE</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white">Goldi</h3>
                    <p className="text-amber-400 font-semibold">AI Chief Financial Officer</p>
                    <div className="mt-4 space-y-2 text-sm text-gray-300">
                      <div className="flex justify-between">
                        <span>Processing Speed:</span>
                        <span className="text-green-400">10,000+ transactions/sec</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Accuracy Rate:</span>
                        <span className="text-green-400">99.97%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Specialization:</span>
                        <span className="text-amber-400">Precious Metals Finance</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Real-time Analysis:</span>
                        <span className="text-green-400">24/7 Active</span>
                      </div>
                    </div>
                    <div className="mt-4 bg-amber-500/10 border border-amber-500/20 rounded p-3">
                      <p className="text-amber-300 text-xs">
                        "Advanced AI system dedicated to optimizing gold trading profitability through 
                        intelligent cost analysis, risk assessment, and predictive financial modeling."
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-white text-center font-medium">{photoModal.imageAlt}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Customer Dashboard Component
const CustomerDashboard = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [goldPrice, setGoldPrice] = useState(null);
  const [newOrder, setNewOrder] = useState({
    product_type: '',
    weight_oz: '',
    notes: ''
  });

  useEffect(() => {
    fetchOrders();
    fetchGoldPrice();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchGoldPrice = async () => {
    try {
      console.log('🔄 Fetching gold price...', new Date().toLocaleTimeString());
      // Add cache-busting parameter to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/gold/current-price?t=${timestamp}`);
      console.log('✅ Gold price fetched:', response.data);
      // Update the timestamp to current time for display purposes
      const updatedGoldPrice = {
        ...response.data,
        displayTimestamp: new Date()
      };
      setGoldPrice(updatedGoldPrice);
    } catch (error) {
      console.error('❌ Error fetching gold price:', error);
      console.error('API URL:', `${API}/gold/current-price`);
    }
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    if (!goldPrice || !newOrder.product_type || !newOrder.weight_oz || !newOrder.quantity) return;

    try {
      const unitWeightInOz = parseFloat(newOrder.weight_oz);
      const quantity = parseInt(newOrder.quantity);
      const totalWeightInOz = unitWeightInOz * quantity;
      const pricePerOz = goldPrice.price_per_ounce;
      const totalAmount = totalWeightInOz * pricePerOz;

      const orderData = {
        ...newOrder,
        quantity: quantity,
        weight_oz: totalWeightInOz, // Total weight for the order
        unit_weight_oz: unitWeightInOz, // Weight per unit
        price_per_ounce: pricePerOz,
        total_amount: totalAmount
      };

      await axios.post(`${API}/orders`, orderData);
      setNewOrder({ product_type: '', weight_oz: '', quantity: '', notes: '' });
      fetchOrders();
      alert(`Order placed successfully!\nQuantity: ${quantity} units\nTotal Weight: ${totalWeightInOz.toFixed(3)} oz\nTotal: $${totalAmount.toLocaleString()}`);
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error placing order. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/20';
      case 'confirmed': return 'bg-blue-500/20 text-blue-400 border-blue-500/20';
      case 'shipped': return 'bg-purple-500/20 text-purple-400 border-purple-500/20';
      case 'delivered': return 'bg-green-500/20 text-green-400 border-green-500/20';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'confirmed': return <CheckCircle className="h-4 w-4" />;
      case 'shipped': return <Truck className="h-4 w-4" />;
      case 'delivered': return <Package className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Welcome back, {user?.full_name}</h1>
          <p className="text-gray-400">Manage your gold investments and track your orders</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Portfolio Value Card */}
          <Card className="bg-gradient-to-r from-amber-500/10 to-yellow-600/10 border-amber-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-amber-400 flex items-center">
                <Coins className="h-5 w-5 mr-2" />
                Portfolio Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">
                ${orders.reduce((total, order) => total + order.total_amount, 0).toLocaleString()}
              </div>
              <p className="text-sm text-gray-400 mt-1">Total investment</p>
            </CardContent>
          </Card>

          {/* Current Gold Price Card */}
          {goldPrice && (
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  Live Gold Price
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  ${goldPrice.price_per_ounce.toLocaleString()}
                </div>
                <p className="text-sm text-gray-400 mt-1">per troy ounce</p>
              </CardContent>
            </Card>
          )}

          {/* Total Orders Card */}
          <Card className="bg-gray-900/50 border-gray-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center">
                <ShoppingCart className="h-5 w-5 mr-2" />
                Total Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{orders.length}</div>
              <p className="text-sm text-gray-400 mt-1">orders placed</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="bg-gray-800 border-gray-700">
            <TabsTrigger value="orders" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              My Orders
            </TabsTrigger>
            <TabsTrigger value="new-order" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              New Order
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              Profile
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Order History</CardTitle>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No orders yet. Create your first order to get started!</p>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="text-white font-semibold">{formatProductName(order.product_type)}</h3>
                            <p className="text-gray-400 text-sm">Order #{order.id.slice(0, 8)}</p>
                          </div>
                          <Badge className={getStatusColor(order.status)}>
                            {getStatusIcon(order.status)}
                            <span className="ml-1 capitalize">{order.status}</span>
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                          <div>
                            <p className="text-gray-400">Quantity</p>
                            <p className="text-white">{order.quantity || 1} {(order.quantity || 1) === 1 ? 'unit' : 'units'}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Total Weight</p>
                            <p className="text-white">{order.weight_oz} oz</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Price/oz</p>
                            <p className="text-white">${order.price_per_ounce.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Total</p>
                            <p className="text-white font-semibold">${order.total_amount.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Date</p>
                            <p className="text-white">{new Date(order.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        {order.notes && (
                          <div className="mt-3 pt-3 border-t border-gray-700">
                            <p className="text-gray-400 text-sm">Notes: {order.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="new-order">
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Create New Order</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleOrderSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="product_type" className="text-gray-300">Gold Product</Label>
                    <select
                      id="product_type"
                      value={newOrder.product_type}
                      onChange={(e) => {
                        const selectedType = e.target.value;
                        let troyOunces = '';
                        
                        if (selectedType === '1_kilo_bar') {
                          troyOunces = '32.15';
                        } else if (selectedType === '12.5_kilo_bar') {
                          troyOunces = '401.875'; // 12.5 * 32.15
                        }
                        
                        setNewOrder({
                          ...newOrder, 
                          product_type: selectedType,
                          weight_oz: troyOunces
                        });
                      }}
                      required
                      className="w-full p-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                    >
                      <option value="">Select gold product</option>
                      <option value="1_kilo_bar">1 Kilo Bar (32.15 troy oz)</option>
                      <option value="12.5_kilo_bar">12.5 Kilo Bar (401.875 troy oz)</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="quantity" className="text-gray-300">Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      step="1"
                      value={newOrder.quantity || ''}
                      onChange={(e) => setNewOrder({...newOrder, quantity: e.target.value})}
                      required
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Enter quantity (e.g., 5 for 5 kilo bars)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="weight_oz" className="text-gray-300">Total Troy Ounces (Calculated)</Label>
                    <Input
                      id="weight_oz"
                      type="number"
                      step="0.001"
                      value={newOrder.weight_oz && newOrder.quantity ? (parseFloat(newOrder.weight_oz) * parseInt(newOrder.quantity || 1)).toFixed(3) : newOrder.weight_oz}
                      readOnly
                      className="bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed"
                      placeholder="Select product and quantity above"
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes" className="text-gray-300">Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      value={newOrder.notes}
                      onChange={(e) => setNewOrder({...newOrder, notes: e.target.value})}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="Any special requirements or notes..."
                    />
                  </div>
                  {goldPrice && newOrder.weight_oz && newOrder.product_type && newOrder.quantity && (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                      <div className="text-white">
                        <p className="text-sm text-amber-400 mb-3 font-semibold">Order Summary:</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-gray-400 text-sm">Product:</p>
                            <p className="font-medium">
                              {newOrder.product_type === '1_kilo_bar' && '1 Kilo Gold Bar'}
                              {newOrder.product_type === '12.5_kilo_bar' && '12.5 Kilo Gold Bar'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-400 text-sm">Quantity:</p>
                            <p className="font-medium">{newOrder.quantity} {newOrder.quantity == 1 ? 'unit' : 'units'}</p>
                          </div>
                          <div>
                            <p className="text-gray-400 text-sm">Total Weight:</p>
                            <p className="font-medium">
                              {(parseFloat(newOrder.weight_oz) * parseInt(newOrder.quantity)).toFixed(3)} troy oz
                            </p>
                          </div>
                        </div>
                        
                        <div className="border-t border-amber-500/20 pt-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-400">Gold Spot Price:</span>
                            <span className="font-medium">${goldPrice.price_per_ounce.toLocaleString()}/oz</span>
                          </div>
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-gray-400">Calculation:</span>
                            <span className="text-sm">
                              {parseFloat(newOrder.weight_oz).toFixed(3)} oz × {newOrder.quantity} × ${goldPrice.price_per_ounce.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-lg font-bold text-amber-400 border-t border-amber-500/20 pt-3">
                            <span>Total Amount:</span>
                            <span>${(parseFloat(newOrder.weight_oz) * parseInt(newOrder.quantity) * goldPrice.price_per_ounce).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-black">
                    Place Order
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profile">
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Profile Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Full Name</Label>
                    <p className="text-white p-2 bg-gray-800 rounded border border-gray-700">{user?.full_name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-300">Email</Label>
                    <p className="text-white p-2 bg-gray-800 rounded border border-gray-700">{user?.email}</p>
                  </div>
                  <div>
                    <Label className="text-gray-300">Phone</Label>
                    <p className="text-white p-2 bg-gray-800 rounded border border-gray-700">{user?.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-300">Member Since</Label>
                    <p className="text-white p-2 bg-gray-800 rounded border border-gray-700">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                {user?.address && (
                  <div>
                    <Label className="text-gray-300">Address</Label>
                    <p className="text-white p-2 bg-gray-800 rounded border border-gray-700">{user.address}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    fetchAnalytics();
    fetchUsers();
    fetchOrders();
    fetchMessages();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/contact-messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${newStatus}`);
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`);
      fetchOrders();
      fetchAnalytics(); // Refresh analytics after deletion
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Error deleting order. Please try again.');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This will also delete all their orders. This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      fetchUsers();
      fetchOrders(); // Refresh orders as user's orders will be deleted
      fetchAnalytics(); // Refresh analytics after deletion
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user. Please try again.');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/contact-messages/${messageId}`);
      fetchMessages();
      fetchAnalytics(); // Refresh analytics after deletion
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Error deleting message. Please try again.');
    }
  };

  const [passwordChange, setPasswordChange] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordChange.new_password !== passwordChange.confirm_password) {
      alert('New passwords do not match');
      return;
    }
    
    if (passwordChange.new_password.length < 6) {
      alert('New password must be at least 6 characters long');
      return;
    }
    
    try {
      await axios.put(`${API}/change-password`, {
        current_password: passwordChange.current_password,
        new_password: passwordChange.new_password
      });
      
      alert('Password changed successfully!');
      setPasswordChange({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.status === 400) {
        alert('Current password is incorrect or new password is invalid');
      } else {
        alert('Error changing password. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
          <p className="text-gray-400">Manage your gold business operations</p>
        </div>

        {analytics && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-r from-blue-500/10 to-blue-600/10 border-blue-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-blue-400 flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Total Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{analytics.total_users}</div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-green-500/10 to-green-600/10 border-green-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-green-400 flex items-center">
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  Total Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{analytics.total_orders}</div>
                <p className="text-sm text-green-400">
                  {analytics.pending_orders} pending
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 border-amber-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-amber-400 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Total Sales
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  ${analytics.total_sales.toLocaleString()}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-purple-500/10 to-purple-600/10 border-purple-500/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-purple-400 flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Messages
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{analytics.total_messages}</div>
                <p className="text-sm text-purple-400">
                  {analytics.unread_messages} unread
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="orders" className="space-y-6">
          <TabsList className="bg-gray-800 border-gray-700">
            <TabsTrigger value="orders" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              Orders
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              Users
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              Messages
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Order Management</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-700">
                        <TableHead className="text-gray-300">Order ID</TableHead>
                        <TableHead className="text-gray-300">Customer</TableHead>
                        <TableHead className="text-gray-300">Product</TableHead>
                        <TableHead className="text-gray-300">Amount</TableHead>
                        <TableHead className="text-gray-300">Status</TableHead>
                        <TableHead className="text-gray-300">Date</TableHead>
                        <TableHead className="text-gray-300">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id} className="border-gray-700">
                          <TableCell className="text-white font-mono text-sm">
                            #{order.id.slice(0, 8)}
                          </TableCell>
                          <TableCell className="text-white">{order.user_id.slice(0, 8)}</TableCell>
                          <TableCell className="text-white">
                            {formatProductName(order.product_type)} ({order.weight_oz}oz)
                          </TableCell>
                          <TableCell className="text-white">
                            ${order.total_amount.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge className={`capitalize ${
                              order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              order.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                              order.status === 'shipped' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {order.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-white">
                            {new Date(order.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <select
                                value={order.status}
                                onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-sm"
                              >
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                              </select>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteOrder(order.id)}
                                className="border-red-500/20 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">User Management</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-700">
                        <TableHead className="text-gray-300">Name</TableHead>
                        <TableHead className="text-gray-300">Email</TableHead>
                        <TableHead className="text-gray-300">Phone</TableHead>
                        <TableHead className="text-gray-300">Joined</TableHead>
                        <TableHead className="text-gray-300">Status</TableHead>
                        <TableHead className="text-gray-300">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id} className="border-gray-700">
                          <TableCell className="text-white">{user.full_name}</TableCell>
                          <TableCell className="text-white">{user.email}</TableCell>
                          <TableCell className="text-white">{user.phone || 'N/A'}</TableCell>
                          <TableCell className="text-white">
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Badge className={user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteUser(user.id)}
                              disabled={user.is_admin}
                              className="border-red-500/20 text-red-400 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages">
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Contact Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div key={message.id} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-white font-semibold">{message.name}</h3>
                          <p className="text-gray-400 text-sm">{message.email}</p>
                          {message.phone && (
                            <p className="text-gray-400 text-sm">{message.phone}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <div>
                            <Badge className={message.status === 'new' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}>
                              {message.status}
                            </Badge>
                            <p className="text-gray-400 text-sm mt-1">
                              {new Date(message.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteMessage(message.id)}
                            className="border-red-500/20 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="bg-gray-800 rounded p-3">
                        <p className="text-gray-300">{message.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card className="bg-gray-900/50 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Admin Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-w-md">
                  <h3 className="text-lg font-semibold text-white mb-4">Change Password</h3>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <Label htmlFor="current_password" className="text-gray-300">
                        Current Password
                      </Label>
                      <Input
                        id="current_password"
                        type="password"
                        value={passwordChange.current_password}
                        onChange={(e) => setPasswordChange({...passwordChange, current_password: e.target.value})}
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="new_password" className="text-gray-300">
                        New Password (minimum 6 characters)
                      </Label>
                      <Input
                        id="new_password"
                        type="password"
                        value={passwordChange.new_password}
                        onChange={(e) => setPasswordChange({...passwordChange, new_password: e.target.value})}
                        required
                        minLength={6}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="confirm_password" className="text-gray-300">
                        Confirm New Password
                      </Label>
                      <Input
                        id="confirm_password"
                        type="password"
                        value={passwordChange.confirm_password}
                        onChange={(e) => setPasswordChange({...passwordChange, confirm_password: e.target.value})}
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                    >
                      Change Password
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// AI CFO Dashboard Component
const AICFODashboard = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [goldPrice, setGoldPrice] = useState(null);
  const [financialData, setFinancialData] = useState({
    totalRevenue: 0,
    totalCosts: 0,
    netProfit: 0,
    transactions: []
  });
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [invalidOrders, setInvalidOrders] = useState([]);
  const [showInvalidOrders, setShowInvalidOrders] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchGoldPrice();
    fetchInvalidOrders();
  }, []);

  useEffect(() => {
    if (orders.length > 0 && goldPrice) {
      calculateFinancials();
    }
  }, [orders, goldPrice, currentMonth, currentYear]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchGoldPrice = async () => {
    try {
      console.log('🔄 Fetching gold price...', new Date().toLocaleTimeString());
      // Add cache-busting parameter to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API}/gold/current-price?t=${timestamp}`);
      console.log('✅ Gold price fetched:', response.data);
      // Update the timestamp to current time for display purposes
      const updatedGoldPrice = {
        ...response.data,
        displayTimestamp: new Date()
      };
      setGoldPrice(updatedGoldPrice);
    } catch (error) {
      console.error('❌ Error fetching gold price:', error);
      console.error('API URL:', `${API}/gold/current-price`);
    }
  };

  const fetchInvalidOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/invalid-orders`);
      setInvalidOrders(response.data.invalid_orders || []);
      return response.data;
    } catch (error) {
      console.error('Error fetching invalid orders:', error);
      return { invalid_orders: [] };
    }
  };

  const deleteInvalidOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this invalid order? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`);
      // Refresh data after deletion
      await fetchInvalidOrders();
      await fetchOrders();
      alert('Invalid order deleted successfully!');
    } catch (error) {
      console.error('Error deleting invalid order:', error);
      alert('Error deleting invalid order. Please try again.');
    }
  };

  const cleanupAllInvalidOrders = async () => {
    if (!window.confirm('Are you sure you want to delete ALL invalid orders? This action cannot be undone and will clean up all problematic data.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/cleanup-invalid-orders`);
      alert(`Successfully cleaned up ${response.data.deleted_count} invalid orders!`);
      // Refresh data after cleanup
      await fetchInvalidOrders();
      await fetchOrders();
    } catch (error) {
      console.error('Error cleaning up invalid orders:', error);
      alert('Error cleaning up invalid orders. Please try again.');
    }
  };

  const calculateFinancials = () => {
    const goldAcquisitionCostPerKilo = 80000; // $80,000 per kilo
    const troyOuncesPerKilo = 32.15;
    const goldAcquisitionCostPerOz = goldAcquisitionCostPerKilo / troyOuncesPerKilo; // ~$2,489 per oz

    let totalRevenue = 0;
    let totalCosts = 0;
    let transactions = [];

    // Filter orders for current month/year
    const filteredOrders = orders.filter(order => {
      const orderDate = new Date(order.created_at);
      return orderDate.getMonth() === currentMonth && orderDate.getFullYear() === currentYear;
    });

    filteredOrders.forEach(order => {
      const revenue = order.total_amount;
      const acquisitionCost = order.weight_oz * goldAcquisitionCostPerOz;
      const transportCost = revenue * 0.05; // 5% of total value
      const insuranceCost = revenue * 0.05; // 5% of total value
      const totalOrderCost = acquisitionCost + transportCost + insuranceCost;
      const profit = revenue - totalOrderCost;

      totalRevenue += revenue;
      totalCosts += totalOrderCost;

      transactions.push({
        orderId: order.id,
        date: new Date(order.created_at).toLocaleDateString(),
        time: new Date(order.created_at).toLocaleTimeString(),
        productType: order.product_type,
        quantity: order.quantity || 1,
        weightOz: order.weight_oz,
        revenue: revenue,
        acquisitionCost: acquisitionCost,
        transportCost: transportCost,
        insuranceCost: insuranceCost,
        totalCost: totalOrderCost,
        profit: profit,
        profitMargin: ((profit / revenue) * 100).toFixed(2)
      });
    });

    setFinancialData({
      totalRevenue,
      totalCosts,
      netProfit: totalRevenue - totalCosts,
      transactions
    });
  };

  const exportToExcel = () => {
    try {
      console.log('Export function called');
      console.log('Financial data:', financialData);
      
      const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"];
      const monthName = monthNames[currentMonth];
      
      // Create CSV content
      let csvContent = `Aurelia Prosperity Gold - Financial Report\n`;
      csvContent += `AI CFO: Goldi\n`;
      csvContent += `Period: ${monthName} ${currentYear}\n`;
      csvContent += `Generated: ${new Date().toLocaleDateString()}\n\n`;
      
      csvContent += `PROFIT & LOSS SUMMARY\n`;
      csvContent += `Total Revenue,$${financialData.totalRevenue.toLocaleString()}\n`;
      csvContent += `Total Costs,$${financialData.totalCosts.toLocaleString()}\n`;
      csvContent += `Net Profit,$${financialData.netProfit.toLocaleString()}\n`;
      csvContent += `Profit Margin,${financialData.totalRevenue > 0 ? ((financialData.netProfit / financialData.totalRevenue) * 100).toFixed(2) : '0.00'}%\n\n`;
      
      csvContent += `DETAILED TRANSACTIONS\n`;
      csvContent += `Order ID,Date,Time,Product Type,Quantity,Weight (oz),Revenue,Acquisition Cost,Transport Cost,Insurance Cost,Total Cost,Profit,Profit Margin\n`;
      
      if (financialData.transactions && financialData.transactions.length > 0) {
        financialData.transactions.forEach(transaction => {
          csvContent += `${transaction.orderId.slice(0, 8)},${transaction.date},${transaction.time},${transaction.productType},${transaction.quantity},${transaction.weightOz},${transaction.revenue},${transaction.acquisitionCost.toFixed(2)},${transaction.transportCost.toFixed(2)},${transaction.insuranceCost.toFixed(2)},${transaction.totalCost.toFixed(2)},${transaction.profit.toFixed(2)},${transaction.profitMargin}%\n`;
        });
      } else {
        csvContent += `No transactions for selected period\n`;
      }

      console.log('CSV content created, length:', csvContent.length);

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Aurelia_Gold_Financial_Report_${monthName}_${currentYear}.csv`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      console.log('Link created and added to DOM');
      
      link.click();
      console.log('Link clicked');
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.log('Cleanup completed');
      }, 100);
      
      // Show success message
      alert('Financial report exported successfully!');
      
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting report: ' + error.message);
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 text-center">
          <div className="bg-gradient-to-r from-amber-400 to-yellow-600 p-4 rounded-lg mb-4 inline-block">
            <svg className="w-12 h-12 text-black" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Goldi AI CFO</h1>
          <p className="text-amber-400">Advanced Financial Analytics & Reporting</p>
        </div>

        {/* Period Selector */}
        <div className="mb-6 flex justify-center space-x-4">
          <select 
            value={currentMonth} 
            onChange={(e) => setCurrentMonth(parseInt(e.target.value))}
            className="bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
          >
            {monthNames.map((month, index) => (
              <option key={index} value={index}>{month}</option>
            ))}
          </select>
          <select 
            value={currentYear} 
            onChange={(e) => setCurrentYear(parseInt(e.target.value))}
            className="bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
          >
            <option value={2024}>2024</option>
            <option value={2025}>2025</option>
          </select>
          <Button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700">
            📊 Export to Excel
          </Button>
        </div>

        {/* Financial Overview */}
        <Card className="bg-gray-900/50 border-gray-700 mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-amber-400">
                  {monthNames[currentMonth]} {currentYear}
                </div>
                <div className="text-sm text-gray-400">Reporting Period</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {financialData.transactions.length}
                </div>
                <div className="text-sm text-gray-400">Total Orders</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {financialData.transactions.reduce((sum, t) => sum + (t.quantity || 1), 0)}
                </div>
                <div className="text-sm text-gray-400">Units Sold</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {financialData.transactions.reduce((sum, t) => sum + t.weightOz, 0).toFixed(1)} oz
                </div>
                <div className="text-sm text-gray-400">Gold Traded</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invalid Orders Management */}
        <Card className="bg-red-900/20 border-red-500/40 mb-6">
          <CardHeader>
            <CardTitle className="text-red-400 flex items-center justify-between">
              <div className="flex items-center">
                ⚠️ Data Quality Management
                {invalidOrders.length > 0 && (
                  <Badge className="ml-2 bg-red-500/20 text-red-300">
                    {invalidOrders.length} Issues Found
                  </Badge>
                )}
              </div>
              <div className="flex space-x-2">
                <Button 
                  onClick={() => {
                    fetchInvalidOrders();
                    setShowInvalidOrders(!showInvalidOrders);
                  }}
                  variant="outline" 
                  size="sm"
                  className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                >
                  {showInvalidOrders ? '👁️ Hide Issues' : '🔍 Scan for Issues'}
                </Button>
                {invalidOrders.length > 0 && (
                  <Button 
                    onClick={cleanupAllInvalidOrders}
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    🧹 Clean All
                  </Button>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          {showInvalidOrders && (
            <CardContent>
              {invalidOrders.length === 0 ? (
                <div className="text-center py-6">
                  <div className="text-4xl text-green-400 mb-2">✅</div>
                  <p className="text-green-400 font-semibold">No Data Quality Issues Found</p>
                  <p className="text-gray-400 text-sm">All orders have valid quantities, weights, and prices.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-red-300 mb-4">
                    Found {invalidOrders.length} orders with data quality issues that are causing artificial losses in P&L calculations:
                  </p>
                  <div className="max-h-64 overflow-y-auto">
                    {invalidOrders.map((order, index) => (
                      <div key={order.id} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-red-400 font-mono text-sm">
                              {order.id.slice(0, 8)}...
                            </span>
                            <Badge className={`text-xs ${order.severity === 'high' ? 'bg-red-600/20 text-red-300' : 'bg-yellow-600/20 text-yellow-300'}`}>
                              {order.severity.toUpperCase()}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-300 grid grid-cols-2 md:grid-cols-4 gap-2">
                            <span>Product: {order.product_type ? formatProductName(order.product_type) : 'N/A'}</span>
                            <span>Qty: {order.quantity}</span>
                            <span>Weight: {order.weight_oz} oz</span>
                            <span>Total: ${order.total_amount}</span>
                          </div>
                          <div className="text-xs text-red-300 mt-1">
                            Issues: {order.issues.join(', ')}
                          </div>
                        </div>
                        <Button
                          onClick={() => deleteInvalidOrder(order.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-500/20 text-red-400 hover:bg-red-500/10 ml-4"
                        >
                          🗑️ Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mt-4">
                    <p className="text-red-300 text-sm">
                      💡 <strong>Why These Orders Cause Negative P&L:</strong> Orders with negative or zero quantities, weights, or prices create artificial losses 
                      in profit calculations. Cleaning these up will show accurate financial performance.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Financial Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Total Revenue Card */}
          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/30 border-green-500/40 hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-green-300 flex items-center text-lg font-semibold">
                <div className="bg-green-500/20 p-2 rounded-lg mr-3">
                  💰
                </div>
                Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-4xl font-bold text-white mb-2">
                ${financialData.totalRevenue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-green-200">
                  {financialData.transactions.length} transactions
                </span>
                <span className="text-green-300 font-medium">
                  +${financialData.totalRevenue > 0 ? (financialData.totalRevenue / financialData.transactions.length).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : '0'} avg
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Total Costs Card */}
          <Card className="bg-gradient-to-br from-red-500/20 to-red-600/30 border-red-500/40 hover:shadow-lg hover:shadow-red-500/20 transition-all duration-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-red-300 flex items-center text-lg font-semibold">
                <div className="bg-red-500/20 p-2 rounded-lg mr-3">
                  💸
                </div>
                Total Costs
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-4xl font-bold text-white mb-2">
                ${financialData.totalCosts.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              </div>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-red-200">Acquisition:</span>
                  <span className="text-red-300">
                    ${(financialData.totalCosts * 0.77).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-red-200">Transport & Insurance:</span>
                  <span className="text-red-300">
                    ${(financialData.totalCosts * 0.23).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Net Profit Card */}
          <Card className={`bg-gradient-to-br ${financialData.netProfit >= 0 ? 'from-amber-500/20 to-amber-600/30 border-amber-500/40 hover:shadow-amber-500/20' : 'from-gray-500/20 to-gray-600/30 border-gray-500/40 hover:shadow-gray-500/20'} hover:shadow-lg transition-all duration-300`}>
            <CardHeader className="pb-2">
              <CardTitle className={`${financialData.netProfit >= 0 ? 'text-amber-300' : 'text-gray-300'} flex items-center text-lg font-semibold`}>
                <div className={`${financialData.netProfit >= 0 ? 'bg-amber-500/20' : 'bg-gray-500/20'} p-2 rounded-lg mr-3`}>
                  {financialData.netProfit >= 0 ? '📈' : '📉'}
                </div>
                Net Profit
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className={`text-4xl font-bold mb-2 ${financialData.netProfit >= 0 ? 'text-white' : 'text-gray-300'}`}>
                ${financialData.netProfit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              </div>
              <div className="space-y-1">
                <div className={`text-lg font-semibold ${financialData.netProfit >= 0 ? 'text-amber-300' : 'text-gray-400'}`}>
                  Margin: {financialData.totalRevenue > 0 ? ((financialData.netProfit / financialData.totalRevenue) * 100).toFixed(2) : '0.00'}%
                </div>
                <div className="text-sm text-gray-400">
                  {financialData.netProfit >= 0 ? 'Profitable operations' : 'Operating at loss'}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* P&L Statement Table */}
        <Card className="bg-gray-900/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              📋 Profit & Loss Statement - {monthNames[currentMonth]} {currentYear}
              <Button onClick={exportToExcel} variant="outline" size="sm">
                📥 Export
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {financialData.transactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-amber-500/30 bg-gray-800/50">
                      <th className="text-left text-amber-300 font-semibold p-3">Order ID</th>
                      <th className="text-left text-amber-300 font-semibold p-3">Date</th>
                      <th className="text-left text-amber-300 font-semibold p-3">Product</th>
                      <th className="text-right text-amber-300 font-semibold p-3">Qty</th>
                      <th className="text-right text-amber-300 font-semibold p-3">Weight (oz)</th>
                      <th className="text-right text-amber-300 font-semibold p-3">Revenue</th>
                      <th className="text-right text-amber-300 font-semibold p-3">Acquisition</th>
                      <th className="text-right text-amber-300 font-semibold p-3">Transport</th>
                      <th className="text-right text-amber-300 font-semibold p-3">Insurance</th>
                      <th className="text-right text-amber-300 font-semibold p-3">Total Cost</th>
                      <th className="text-right text-amber-300 font-semibold p-3">Profit</th>
                      <th className="text-right text-amber-300 font-semibold p-3">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financialData.transactions.map((transaction, index) => {
                      // Check if this order is invalid
                      const isInvalidOrder = invalidOrders.find(inv => inv.id === transaction.orderId);
                      
                      return (
                        <tr key={index} className={`border-b border-gray-800 hover:bg-gray-800/30 transition-colors ${isInvalidOrder ? 'bg-red-500/10' : ''}`}>
                          <td className="text-gray-300 p-3 font-mono text-xs">
                            <div className="flex items-center space-x-1">
                              <span>{transaction.orderId.slice(0, 8)}...</span>
                              {isInvalidOrder && (
                                <div className="flex items-center space-x-1">
                                  <span className="text-red-400 text-xs" title={`Issues: ${isInvalidOrder.issues.join(', ')}`}>
                                    ⚠️
                                  </span>
                                  <button
                                    onClick={() => deleteInvalidOrder(transaction.orderId)}
                                    className="text-red-400 hover:text-red-300 text-xs"
                                    title="Delete invalid order"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        <td className="text-white p-3">{transaction.date}</td>
                        <td className="text-white p-3">
                          <span className="bg-gray-700 px-2 py-1 rounded text-xs">
                            {formatProductName(transaction.productType).toUpperCase()}
                          </span>
                        </td>
                        <td className="text-right text-white p-3 font-semibold">{transaction.quantity}</td>
                        <td className="text-right text-white p-3">{transaction.weightOz.toFixed(3)}</td>
                        <td className="text-right text-green-400 p-3 font-semibold">
                          ${transaction.revenue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </td>
                        <td className="text-right text-red-300 p-3">
                          ${transaction.acquisitionCost.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </td>
                        <td className="text-right text-red-300 p-3">
                          ${transaction.transportCost.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </td>
                        <td className="text-right text-red-300 p-3">
                          ${transaction.insuranceCost.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </td>
                        <td className="text-right text-red-400 p-3 font-semibold">
                          ${transaction.totalCost.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </td>
                        <td className={`text-right p-3 font-bold ${transaction.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ${Math.abs(transaction.profit).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                          {transaction.profit < 0 && <span className="text-xs ml-1">(Loss)</span>}
                        </td>
                        <td className={`text-right p-3 font-semibold ${transaction.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {transaction.profitMargin}%
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-amber-500/30 bg-gray-800/30">
                      <td colSpan="5" className="text-right text-amber-300 font-semibold p-3">TOTALS:</td>
                      <td className="text-right text-green-400 font-bold p-3">
                        ${financialData.totalRevenue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </td>
                      <td colSpan="3" className="text-center text-gray-400 p-3">-</td>
                      <td className="text-right text-red-400 font-bold p-3">
                        ${financialData.totalCosts.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </td>
                      <td className={`text-right font-bold p-3 ${financialData.netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${Math.abs(financialData.netProfit).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        {financialData.netProfit < 0 && <span className="text-xs ml-1">(Loss)</span>}
                      </td>
                      <td className={`text-right font-bold p-3 ${financialData.netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {financialData.totalRevenue > 0 ? ((financialData.netProfit / financialData.totalRevenue) * 100).toFixed(2) : '0.00'}%
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl text-gray-600 mb-4">📊</div>
                <h3 className="text-xl text-gray-400 mb-2">No Financial Data</h3>
                <p className="text-gray-500">
                  No transactions found for {monthNames[currentMonth]} {currentYear}.
                  <br />
                  Select a different period or create some orders to view financial data.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  const { user, loading } = useAuth();
  const [notification, setNotification] = useState('');

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="bg-gradient-to-r from-amber-400 to-yellow-600 p-3 rounded-lg mb-4 inline-block">
            <Crown className="h-8 w-8 text-black animate-pulse" />
          </div>
          <p className="text-amber-400">Loading...</p>
        </div>
      </div>
    );
  }

  const handleNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(''), 5000);
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-20 right-4 z-50 max-w-md">
          <Alert className="border-amber-500/20 bg-amber-500/10">
            <AlertCircle className="h-4 w-4 text-amber-400" />
            <AlertDescription className="text-amber-300">{notification}</AlertDescription>
          </Alert>
        </div>
      )}
      
      <div className="container mx-auto px-4">
        {/* Email Verification Banner */}
        {user && (
          <EmailVerificationBanner 
            user={user} 
            onResend={handleNotification}
          />
        )}
        
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route 
            path="/dashboard" 
            element={
              user ? (
                user.is_ai_cfo ? <AICFODashboard /> : 
                user.is_admin ? <AdminDashboard /> : 
                <CustomerDashboard />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
        </Routes>
      </div>
    </div>
  );
};

// Email Verification Page
const EmailVerificationPage = () => {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      if (!token) {
        setError('No verification token provided');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/verify-email/${token}`);
        setMessage(response.data.message);
      } catch (error) {
        setError(error.response?.data?.detail || 'Verification failed');
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, []);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <Card className="bg-gray-900 border-amber-500/20">
          <CardHeader className="text-center">
            <div className="bg-gradient-to-r from-amber-400 to-yellow-600 p-3 rounded-lg mb-4 inline-block">
              <Mail className="h-8 w-8 text-black" />
            </div>
            <CardTitle className="text-white">Email Verification</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            {loading ? (
              <div className="space-y-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
                <p className="text-gray-300">Verifying your email...</p>
              </div>
            ) : message ? (
              <div className="space-y-4">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                <p className="text-green-400 font-medium">{message}</p>
                <Link to="/dashboard">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-black">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
                <p className="text-red-400 font-medium">{error}</p>
                <Link to="/">
                  <Button variant="outline" className="border-amber-500 text-amber-500">
                    Return Home
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Password Reset Page
const PasswordResetPage = () => {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <Card className="bg-gray-900 border-amber-500/20">
          <CardHeader className="text-center">
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-3 rounded-lg mb-4 inline-block">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-white">Reset Your Password</CardTitle>
          </CardHeader>
          <CardContent>
            <PasswordResetDialog open={true} onClose={() => window.location.href = '/'} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Terms of Use Page
const TermsOfUsePage = () => {
  return (
    <div className="min-h-screen bg-black py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gray-900/90 border-amber-500/20">
            <CardHeader className="text-center border-b border-amber-500/20">
              <div className="bg-gradient-to-r from-amber-500 to-yellow-600 p-3 rounded-lg mb-4 inline-block">
                <Shield className="h-8 w-8 text-black" />
              </div>
              <CardTitle className="text-white text-3xl">Terms of Use</CardTitle>
              <p className="text-amber-200 mt-2">Aurelia Prosperity Gold - Effective Date: January 1, 2025</p>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6 text-gray-300 leading-relaxed">
                
                <section>
                  <h2 className="text-xl font-semibold text-white mb-3 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2 text-amber-500" />
                    1. Acceptance of Terms
                  </h2>
                  <p>
                    By accessing, browsing, or using the website https://aureliaprosperity.gold (the "Website") or any services offered by Aurelia Prosperity Gold ("Company", "we", "us", "our"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Use ("Terms") and our Privacy Policy. If you do not agree to these Terms, you must not use our Website or services.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">2. Description of Services</h2>
                  <p className="mb-3">
                    Aurelia Prosperity Gold provides information and facilitates connections related to precious metals trading. Our services include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Providing market information and educational content about precious metals</li>
                    <li>Facilitating contact between users and authorized precious metals dealers</li>
                    <li>Offering consultation and advisory services regarding precious metals</li>
                    <li>Operating trading platforms and related technology services</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">3. Eligibility and User Requirements</h2>
                  <div className="bg-amber-500/10 border-l-4 border-amber-500 p-4 rounded-r-lg mb-4">
                    <p><strong>Age Requirements:</strong> You must be at least 18 years old to use our services. By using our Website, you represent that you meet this age requirement.</p>
                  </div>
                  <p className="mb-3">
                    <strong>Capacity:</strong> You represent that you have the legal capacity to enter into binding agreements and that your use of our services will not violate any applicable laws or regulations in your jurisdiction.
                  </p>
                  <p>
                    <strong>Prohibited Users:</strong> Our services are not available in jurisdictions where precious metals trading is prohibited or restricted by law.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">4. User Responsibilities and Prohibited Conduct</h2>
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-lg font-medium text-amber-300 mb-2">You agree NOT to:</h3>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Use our services for any unlawful purpose or in violation of applicable laws</li>
                        <li>Provide false, misleading, or fraudulent information</li>
                        <li>Engage in money laundering, terrorist financing, or other financial crimes</li>
                        <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                        <li>Transmit viruses, malware, or other harmful code</li>
                        <li>Interfere with the proper functioning of our Website or services</li>
                        <li>Use our services to facilitate prohibited transactions under applicable sanctions laws</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">5. Financial Disclaimers and Investment Risks</h2>
                  <div className="bg-red-500/10 border-l-4 border-red-500 p-4 rounded-r-lg mb-4">
                    <p><strong>Important:</strong> Aurelia Prosperity Gold does not hold an Australian Financial Services Licence (AFSL), European investment services licence, or similar authorizations in other jurisdictions. We do not provide investment advice, financial advice, or recommendations.</p>
                  </div>
                  <p className="mb-3">
                    <strong>Risk Warning:</strong> Trading in precious metals involves significant risk and may not be suitable for all investors. Prices can be volatile and may result in substantial losses. You should:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                    <li>Carefully consider your financial situation and risk tolerance</li>
                    <li>Seek independent financial, legal, and tax advice</li>
                    <li>Only invest what you can afford to lose</li>
                    <li>Understand that past performance does not guarantee future results</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">6. Intellectual Property Rights</h2>
                  <p className="mb-3">
                    All content on our Website, including text, graphics, logos, images, software, and design elements, is the property of Aurelia Prosperity Gold or its licensors and is protected by international copyright, trademark, and other intellectual property laws.
                  </p>
                  <p>
                    You may view, download, and print content for personal, non-commercial use only. You may not modify, distribute, reproduce, or create derivative works without our written permission.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">7. Privacy and Data Protection</h2>
                  <p className="mb-3">
                    Our collection, use, and protection of your personal information is governed by our Privacy Policy, which complies with:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Australian Privacy Principles (Privacy Act 1988)</li>
                    <li>European General Data Protection Regulation (GDPR)</li>
                    <li>California Consumer Privacy Act (CCPA)</li>
                    <li>Other applicable data protection laws</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">8. Limitation of Liability</h2>
                  <div className="bg-yellow-500/10 border-l-4 border-yellow-500 p-4 rounded-r-lg mb-4">
                    <p><strong>To the maximum extent permitted by law:</strong> Aurelia Prosperity Gold shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of our services, including but not limited to lost profits, trading losses, or damages resulting from data loss or system failures.</p>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">9. Governing Law and Jurisdiction</h2>
                  <p className="mb-3">
                    <strong>Primary Jurisdiction:</strong> These Terms are governed by the laws of New South Wales, Australia, without regard to conflict of law principles.
                  </p>
                  <p className="mb-3">
                    <strong>Dispute Resolution:</strong> Any disputes shall be subject to the exclusive jurisdiction of the courts of New South Wales, Australia, except where prohibited by local consumer protection laws.
                  </p>
                  <p>
                    <strong>International Users:</strong> Nothing in these Terms affects your statutory rights under local consumer protection laws where such rights cannot be excluded.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">10. Modifications and Termination</h2>
                  <p className="mb-3">
                    We reserve the right to modify these Terms at any time. Changes will be effective upon posting to our Website. Continued use of our services constitutes acceptance of modified Terms.
                  </p>
                  <p>
                    We may suspend or terminate your access to our services at any time, with or without notice, for violation of these Terms or applicable laws.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">11. Contact Information</h2>
                  <div className="bg-blue-500/10 border-l-4 border-blue-500 p-4 rounded-r-lg">
                    <p>For questions regarding these Terms of Use, please contact us:</p>
                    <ul className="mt-2 space-y-1">
                      <li><strong>Email:</strong> info@aureliaprosperity.gold</li>
                      <li><strong>Address:</strong> 50 Pitt Street, Sydney 2000 NSW, Australia</li>
                    </ul>
                  </div>
                </section>

                <div className="text-center pt-6 border-t border-gray-700">
                  <p className="text-xs text-gray-400">
                    Last Updated: January 1, 2025 | Document Version: 1.0
                  </p>
                  <Link to="/" className="text-amber-400 hover:text-amber-300 underline mt-2 inline-block">
                    ← Return to Homepage
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-black py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gray-900/90 border-amber-500/20">
            <CardHeader className="text-center border-b border-amber-500/20">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-3 rounded-lg mb-4 inline-block">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-white text-3xl">Privacy Policy</CardTitle>
              <p className="text-blue-200 mt-2">Aurelia Prosperity Gold - Effective Date: January 1, 2025</p>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6 text-gray-300 leading-relaxed">
                
                <section>
                  <h2 className="text-xl font-semibold text-white mb-3 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2 text-blue-500" />
                    1. Overview and Commitment
                  </h2>
                  <p>
                    Aurelia Prosperity Gold ("Company", "we", "us", "our") is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website https://aureliaprosperity.gold or use our services. This policy complies with privacy laws in Australia, the European Union, the United States, and other jurisdictions where we operate.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-medium text-blue-300 mb-2">Personal Information You Provide:</h3>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li><strong>Contact Information:</strong> Name, email address, phone number, mailing address</li>
                        <li><strong>Account Information:</strong> Username, password (encrypted), profile preferences</li>
                        <li><strong>Financial Information:</strong> Trading preferences, investment goals (no payment card data stored)</li>
                        <li><strong>Communication Records:</strong> Messages, inquiries, support requests</li>
                        <li><strong>Verification Data:</strong> Identity verification documents as required by law</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium text-blue-300 mb-2">Automatically Collected Information:</h3>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system</li>
                        <li><strong>Usage Data:</strong> Pages visited, time spent, click patterns, referral sources</li>
                        <li><strong>Location Data:</strong> General geographic location (country/region level)</li>
                        <li><strong>Cookies:</strong> Session cookies, preference cookies, analytics cookies</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">3. How We Use Your Information</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-500/10 border-l-4 border-green-500 p-4 rounded-r-lg">
                      <h3 className="text-lg font-medium text-green-300 mb-2">Service Provision:</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Account creation and management</li>
                        <li>Processing inquiries and transactions</li>
                        <li>Providing customer support</li>
                        <li>Sending service-related communications</li>
                      </ul>
                    </div>
                    <div className="bg-blue-500/10 border-l-4 border-blue-500 p-4 rounded-r-lg">
                      <h3 className="text-lg font-medium text-blue-300 mb-2">Legal Compliance:</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Anti-money laundering (AML) compliance</li>
                        <li>Know Your Customer (KYC) verification</li>
                        <li>Regulatory reporting requirements</li>
                        <li>Legal process compliance</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">4. Legal Basis for Processing (GDPR Compliance)</h2>
                  <div className="space-y-3">
                    <p><strong>Consent:</strong> Newsletter subscriptions, marketing communications</p>
                    <p><strong>Contract Performance:</strong> Account services, transaction processing</p>
                    <p><strong>Legal Obligation:</strong> AML/KYC compliance, regulatory reporting</p>
                    <p><strong>Legitimate Interest:</strong> Website security, fraud prevention, service improvement</p>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">5. Information Sharing and Disclosure</h2>
                  
                  <div className="bg-red-500/10 border-l-4 border-red-500 p-4 rounded-r-lg mb-4">
                    <p><strong>We do not sell your personal information.</strong> We may share your information only in the following circumstances:</p>
                  </div>

                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Authorized Third Parties:</strong> Licensed precious metals dealers, payment processors (with your consent)</li>
                    <li><strong>Service Providers:</strong> Technology providers, security services, analytics providers (under strict confidentiality agreements)</li>
                    <li><strong>Legal Requirements:</strong> Court orders, regulatory investigations, law enforcement requests</li>
                    <li><strong>Business Transfers:</strong> Merger, acquisition, or sale of business assets (with notice)</li>
                    <li><strong>Protection:</strong> Preventing fraud, protecting rights, safety, or property</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">6. Your Privacy Rights</h2>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <h3 className="text-lg font-medium text-amber-300">All Users:</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Access your personal information</li>
                        <li>Correct inaccurate information</li>
                        <li>Request deletion (subject to legal requirements)</li>
                        <li>Opt-out of marketing communications</li>
                        <li>Request data portability</li>
                      </ul>
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-lg font-medium text-amber-300">EU Residents (GDPR):</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Right to be forgotten</li>
                        <li>Object to processing</li>
                        <li>Restrict processing</li>
                        <li>Lodge complaints with supervisory authorities</li>
                        <li>Withdraw consent</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">7. Data Security and Protection</h2>
                  <div className="bg-green-500/10 border-l-4 border-green-500 p-4 rounded-r-lg mb-4">
                    <p><strong>Security Measures:</strong></p>
                    <ul className="list-disc list-inside space-y-1 mt-2">
                      <li>256-bit SSL encryption for data transmission</li>
                      <li>Encrypted storage of sensitive information</li>
                      <li>Regular security audits and penetration testing</li>
                      <li>Access controls and authentication systems</li>
                      <li>Employee training on data protection</li>
                      <li>Incident response and breach notification procedures</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">8. International Data Transfers</h2>
                  <p className="mb-3">
                    Your information may be transferred to and processed in countries outside your residence jurisdiction. We ensure adequate protection through:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong>EU:</strong> Standard Contractual Clauses (SCCs), adequacy decisions</li>
                    <li><strong>Australia:</strong> Overseas disclosure protections under Privacy Act</li>
                    <li><strong>US:</strong> Privacy Shield successor frameworks, contractual safeguards</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">9. Data Retention</h2>
                  <div className="space-y-3">
                    <p><strong>Account Information:</strong> Retained while account is active plus 7 years (regulatory requirements)</p>
                    <p><strong>Transaction Records:</strong> 7 years from transaction date (financial regulations)</p>
                    <p><strong>Communication Records:</strong> 3 years from last contact</p>
                    <p><strong>Marketing Data:</strong> Until opt-out or 2 years of inactivity</p>
                    <p><strong>Technical Data:</strong> 12 months (analytics) / Session duration (functional cookies)</p>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">10. Cookies and Tracking</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-medium text-blue-300 mb-2">Cookie Types:</h3>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li><strong>Essential:</strong> Required for website functionality</li>
                        <li><strong>Analytics:</strong> Google Analytics (anonymized), performance monitoring</li>
                        <li><strong>Preferences:</strong> Language, display settings</li>
                        <li><strong>Marketing:</strong> With consent only</li>
                      </ul>
                    </div>
                    <p><strong>Cookie Control:</strong> You can manage cookie preferences through your browser settings or our cookie consent banner.</p>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">11. Children's Privacy</h2>
                  <div className="bg-yellow-500/10 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                    <p>Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately for deletion.</p>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">12. Changes to This Policy</h2>
                  <p>
                    We may update this Privacy Policy periodically. Material changes will be communicated through email notification or website notice. Continued use of our services after changes constitutes acceptance of the updated policy.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-white mb-3">13. Contact Information and Rights Requests</h2>
                  <div className="bg-blue-500/10 border-l-4 border-blue-500 p-4 rounded-r-lg">
                    <p className="mb-3"><strong>Privacy Officer / Data Protection Officer:</strong></p>
                    <ul className="space-y-1">
                      <li><strong>Email:</strong> info@aureliaprosperity.gold</li>
                      <li><strong>Address:</strong> 50 Pitt Street, Sydney 2000 NSW, Australia</li>
                    </ul>
                    <p className="mt-3 text-sm"><strong>Response Time:</strong> We will respond to privacy requests within 30 days (or as required by local law).</p>
                  </div>
                </section>

                <div className="text-center pt-6 border-t border-gray-700">
                  <p className="text-xs text-gray-400">
                    Last Updated: January 1, 2025 | Document Version: 1.0
                  </p>
                  <Link to="/" className="text-amber-400 hover:text-amber-300 underline mt-2 inline-block">
                    ← Return to Homepage
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Dedicated Disclaimer Page
const DisclaimerPage = () => {
  return (
    <div className="min-h-screen bg-black py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gray-900/90 border-amber-500/20">
            <CardHeader className="text-center border-b border-amber-500/20">
              <div className="bg-gradient-to-r from-amber-500 to-yellow-600 p-3 rounded-lg mb-4 inline-block">
                <Shield className="h-8 w-8 text-black" />
              </div>
              <CardTitle className="text-white text-3xl">Legal Disclaimer</CardTitle>
              <p className="text-amber-200 mt-2">Aurelia Prosperity Gold - Effective Date: January 1, 2025</p>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <div className="bg-amber-500/10 border-l-4 border-amber-500 p-4 rounded-r-lg">
                  <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2 text-amber-500" />
                    Disclaimer
                  </h3>
                </div>

                <p className="text-sm md:text-base">
                  The information contained on{' '}
                  <span className="text-amber-400 font-medium">https://aureliaprosperity.gold</span>{' '}
                  (the "Website") is provided for general informational purposes only. Nothing on this Website is intended to constitute, and shall not be construed as constituting, an offer, solicitation, or recommendation to acquire, dispose of, or otherwise deal in any financial product or service.
                </p>

                <div className="bg-red-500/10 border-l-4 border-red-500 p-4 rounded-r-lg">
                  <p className="text-sm md:text-base">
                    <strong className="text-red-400">Important Notice:</strong> Aurelia Prosperity does not hold an Australian Financial Services Licence ("AFSL") or any other licence authorising it to trade in gold, precious metals, or related financial products. Where applicable, facilitation of transactions may be conducted through third-party entities duly authorised and licensed under the relevant laws and regulations.
                  </p>
                </div>

                <p className="text-sm md:text-base">
                  References on this Website to certain products, services, or solutions may relate to offerings that are in development, not yet available to the public, or otherwise subject to ongoing regulatory processes. Any statement regarding future availability, expected features, potential outcomes, or anticipated benefits constitutes a forward-looking statement. Such statements are inherently uncertain, are subject to regulatory approval in Australia, and may be influenced by risks, legal constraints, market fluctuations, and factors outside of Aurelia Prosperity's control. Actual results or availability of products and services may differ materially from those expressed or implied.
                </p>

                <div className="bg-yellow-500/10 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                  <p className="text-sm md:text-base">
                    <strong className="text-yellow-400">Liability Disclaimer:</strong> Aurelia Prosperity makes no representation or warranty, express or implied, as to the accuracy, reliability, or completeness of any information provided on this Website, including forward-looking statements. To the maximum extent permitted by law, Aurelia Prosperity disclaims all liability for any loss, damage, cost, or expense incurred by any person in reliance on the information presented.
                  </p>
                </div>

                <p className="text-sm md:text-base">
                  Users are strongly encouraged to conduct their own independent inquiries and, where necessary, obtain professional financial, legal, or other relevant advice prior to engaging in any transaction involving precious metals or related products.
                </p>

                <div className="bg-blue-500/10 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <p className="text-sm md:text-base font-medium text-blue-400">
                    By accessing or using this Website, you acknowledge and agree to the terms of this Disclaimer.
                  </p>
                </div>

                <div className="text-center pt-6 border-t border-gray-700">
                  <p className="text-xs text-gray-400">
                    Last Updated: January 1, 2025 | For questions regarding this disclaimer, please contact{' '}
                    <span className="text-amber-400">info@aureliaprosperity.gold</span>
                  </p>
                  <Link to="/" className="text-amber-400 hover:text-amber-300 underline mt-2 inline-block">
                    ← Return to Homepage
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Root App with Router and Auth Provider
const AppWithProviders = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default AppWithProviders;